// Wallhack Explorer — persistent fullscreen product explorer.
// The explorer boots on connect, fills the viewport permanently, and never
// opens or closes. Scene teardown happens only on disconnect (theme-editor
// section reload). The 3D module is wh-bundle-3d.js — bump its ?v= pin below
// (SCENE_MODULE) on EVERY 3D edit; this file is the pin's only site.
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
// wh-bundle-3d.js (three.js + all the loaders — the heavy part) is loaded
// LAZILY via dynamic import() on first render (#loadSceneModule), NOT at the
// top level, so the payload parse + first paint aren't blocked on three.js.
//
// The ?v= on that dynamic-import specifier is OUR cache-buster, bumped by hand on
// every wh-bundle-3d.js edit. Shopify's CDN caches un-versioned asset URLs for a
// year and does NOT purge on theme push — only this entry file gets an automatic
// ?v= hash via asset_url. Changing the query here changes this file's content,
// which re-hashes the entry, which makes browsers fetch the new 3D module.
const SCENE_MODULE = './wh-bundle-3d.js?v=235';
// The museum's own page. Owned route (2026-09-24): /pages/museum boots and
// routes the MUSEUM catalog in the scene; the legacy page template's grid is
// never lifted into the canvas. Localised via Theme.routes.root_url.
const MUSEUM_PAGE = '/pages/museum';
// Horizon 4.1 cart events: the cart icon / drawer / sections listen for
// StandardEvents.cartLinesUpdate. Every explorer cart write announces through
// the same CartLinesUpdateEvent so they refresh instead of going stale (the
// announce hands over a fresh /cart.js — no second mutation), and the explorer
// LISTENS for the same event so external cart changes re-sync the picks.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Transition-veil timings (ms). The veil is ONE overlay used three ways:
// entry pre-loader (rune — full beat on a cold visit, short on a warm one so
// returning deep-link visitors aren't gated on branding), plain ~0.5s
// crossfade between scenes, and a shorter rune "load screen" into the summary.
const VEIL_FADE_MS = 200; // opacity transition — keep in sync with the CSS
const VEIL_SCENE_HOLD_MS = 80; // scene switch: in + hold + out ≈ 0.5s total
const VEIL_SUMMARY_HOLD_MS = 450; // rune visible ~0.5s on the way to the summary
const PRELOADER_MIN_MS = 1500; // minimum rune time on a COLD entry (branding beat)
const PRELOADER_WARM_MS = 400; // minimum rune time once the session is warm
const WARM_SESSION_KEY = 'wh-ex-warm'; // sessionStorage flag set after first entry
// EVERY canvas view — fetched page or local view, open or switch — runs the
// in-canvas rune for at least this long (user spec 2026-08-05): slower loads
// extend it, instant ones get a consistent transition beat instead of a pop.
const CANVAS_LOADER_MIN_MS = 1000;

class WhExplorer extends Component {
  requiredRefs = ['root'];

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.whBound === '1') return;

    // Theme editor collapse: the section rendered data-editor-collapsed on a
    // content-route template — no boot at all. No listeners, no scene, and
    // crucially no #openContentFromDom DOM-lifting (the editor's HTML checker
    // flags moved section nodes as broken markup). Deliberately BEFORE the
    // whBound stamp: if the merchant toggles the setting, the editor
    // re-renders the section and a live boot must still be possible.
    if (this.hasAttribute('data-editor-collapsed')) return;

    this.dataset.whBound = '1';

    // Router mode. 'full' (index/product/cart templates) = the explorer owns
    // real routes: product/summary switches pushState to /products/<handle>
    // and /cart, and Back/Forward steer the explorer. 'params' (any other
    // template, e.g. a hidden test page) = never touch the path; only the
    // ?wh-bundle= config param is written. Set by the section's Liquid from
    // template.name.
    this.routeMode = this.dataset.routeMode || 'params';
    // Path writes arm on the first USER-driven navigation (#goToStep /
    // product / variant switches) — never on load or passive syncs. A cold
    // load must keep the server-given path ('/' stays '/'), and cart
    // hydration landing after boot must not rewrite it either.
    this._routeArmed = false;
    this._baseTitle = document.title;
    this._sceneBgCurrent = null; // active background-image URL (null = scene color)
    this._sceneBgFlip = false; // which of the two crossfade layers is live
    window.addEventListener('popstate', this.#onPopState);
    window.addEventListener('pageshow', this.#onPageShow);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // The REAL Shopify cart is the source of truth for picks: every ADD /
    // ADD / line qty / line remove write immediately through a serialized
    // queue (concurrent clicks can't interleave), and #applyCart rebuilds
    // state.picks from the cart response.
    this._cartQueue = Promise.resolve();
    this._cart = null;
    // Canvas HTML cache + in-flight prefetch dedupe — shared by every fetch
    // path (#fetchHtml / #prefetchContent).
    this._contentCache = new Map();
    this._prefetching = new Set();

    const dataEl = this.querySelector('script[data-wh-bundle-data]');
    if (!dataEl) {
      console.warn('[wh-explorer] No data payload — section is empty.');
      return;
    }
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[wh-explorer] Failed to parse data payload', e);
      return;
    }

    this.labels = this.data.loadout.labels;
    // Which alternate product template renders each See-more canvas view
    // (merchant-set in the section's Canvas view templates settings).
    this.viewTemplates = this.data.loadout.viewTemplates || {};
    this.bundleLabel = this.data.loadout.name;

    // Category-keyed model per CATALOG. A catalog is one "[WH] Explorer
    // Catalog" metaobject: an ordered list of categories. The primary catalog
    // is the shop; an optional second one is the MUSEUM (past drops, browsed
    // identically — sold-out items just present as SOLD OUT). #installCatalog
    // builds realSteps / stepKeys / categories for the selected catalog, and
    // browsing state is cached per catalog so switching back restores it.
    this.summaryKey = 'summary';
    this.catalogs = [
      { handle: this.data.loadout.handle, name: this.data.loadout.name || '', steps: this.data.steps || [] },
    ];
    const museum = this.data.museum;
    if (
      museum &&
      Array.isArray(museum.steps) &&
      museum.steps.some((s) => s && s.products && s.products.length)
    ) {
      this.catalogs.push({ handle: museum.handle, name: museum.name || 'Museum', steps: museum.steps });
    }
    this._browseCache = {};
    this.state = {
      currentKey: null,
      browse: {},
      // Per-product variant memory: switching shapes (products) restores each
      // product's last-selected variant instead of resetting to its default, so
      // a shape keeps its colour/material/sound (and its 3D model) across switches.
      variantByProduct: {},
      // COMMITTED selection per category ({ productId, variantId, qty,
      // lineKey }) — mirrors the REAL cart's _wh_category lines and is
      // rebuilt from it by #applyCart. PRESENCE in `picks` IS the added flag.
      picks: {},
    };
    this.#installCatalog(0);

    if (this.refs.shareButton) this.refs.shareButton.addEventListener('click', this.#onShare);
    if (this.refs.reviewAddToCart) this.refs.reviewAddToCart.addEventListener('click', this.#onCheckout);
    if (this.refs.moreClose) this.refs.moreClose.addEventListener('click', () => this.#closeMoreNav());
    if (this.refs.reviewClose) {
      // "close" on the cart panel returns to the last real step, not exit.
      this.refs.reviewClose.addEventListener('click', () => this.#closeSummary());
    }
    if (this.refs.cartButton) this.refs.cartButton.addEventListener('click', () => this.#goToStep(this.summaryKey));
    if (this.refs.cartButtonMobile) {
      this.refs.cartButtonMobile.addEventListener('click', () => this.#goToStep(this.summaryKey));
    }
    if (this.refs.catalogSwitch) {
      this.refs.catalogSwitch.addEventListener('click', () => this.#switchCatalog(1 - this._catalogIdx));
    }
    // Nested footer menus: one sub open at a time; toggling a parent shows
    // its children in the footer's right half (elements travel with the nav
    // when it docks to the mobile drawer, so direct bindings persist).
    this.querySelectorAll('[data-wh-footer-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        this.querySelectorAll('[data-wh-footer-toggle]').forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
          const sub = other.nextElementSibling;
          if (sub) sub.hidden = true;
        });
        if (!open) {
          btn.setAttribute('aria-expanded', 'true');
          const sub = btn.nextElementSibling;
          if (sub) sub.hidden = false;
        }
      });
    });
    if (this.refs.mobileNavClose) this.refs.mobileNavClose.addEventListener('click', this.#closeMobileNav);
    if (this.refs.mnavTabProducts) this.refs.mnavTabProducts.addEventListener('click', () => this.#setMobileNavTab('products'));
    if (this.refs.mnavTabSite) this.refs.mnavTabSite.addEventListener('click', () => this.#setMobileNavTab('site'));
    if (this.refs.mobileNav) {
      // The site nav DOCKS in here on phones — its links need the same
      // canvas interception + hover prefetch as in the right drawer.
      this.refs.mobileNav.addEventListener('click', this.#onMenuClick);
      this.refs.mobileNav.addEventListener('pointerover', this.#onLinkHover);
    }
    this.#setupMobileDocks();
    this.#setupViewportGuard();
    this.#setupChatDock();
    if (this.refs.menuButton) this.refs.menuButton.addEventListener('click', this.#openMenu);
    // The wordmark (desktop topbar + phone bottom bar) is a real link to the
    // home URL (user 2026-10-10). On the summary it doubles as CLOSE (user
    // 2026-08-18); in the MUSEUM catalog it returns to the SHOP (user
    // 2026-08-20 — people didn't find the SHOP button on the right intuitive
    // on its own); everywhere else it routes home like any owned link
    // (#interceptLink: closes the canvas/menu, pushes '/', re-seeds the
    // scene — no reload). Outside full route mode the link navigates.
    const onWordmarkClick = (e) => {
      if (this._catalogIdx === 1) {
        e.preventDefault();
        this.#switchCatalog(0);
        return;
      }
      if (this.#isSummary(this.state.currentKey)) {
        e.preventDefault();
        this.#closeSummary();
        return;
      }
      if (this.routeMode !== 'full') return;
      this.#interceptLink(e, { closeMenuOnRoute: true });
    };
    for (const k of ['wordmark', 'wordmarkMobile']) {
      if (this.refs[k]) this.refs[k].addEventListener('click', onWordmarkClick);
    }
    if (this.refs.notifyClose) {
      this.refs.notifyClose.addEventListener('click', () => {
        if (this.refs.notifyDialog && this.refs.notifyDialog.open) this.refs.notifyDialog.close();
      });
    }
    // Post-redirect success: Shopify appends ?customer_posted=true (the
    // form object is invisible outside {% form %}, so no Liquid attribute
    // can carry this). Reopen only on OWNED scene routes — campaign-page
    // posts are the K-001 module's dialog, not ours.
    const notifyPosted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (
      notifyPosted &&
      this.refs.notifyDialog &&
      this.routeMode === 'full' &&
      this.#ownsPath(window.location.pathname)
    ) {
      this.refs.notifyDialog.showModal();
    }
    document.addEventListener('keydown', this.#onSummaryKeydown);
    document.addEventListener('keydown', this.#onSceneKeydown);
    // Account pill: the component's internal trigger only covers the avatar
    // + slotted text — clicks on the REST of the pill (incl. the component's
    // own empty flex area, which dominates when no language selector renders)
    // must forward so the WHOLE button activates (user 2026-08-14 + -17).
    // composedPath sees through the shadow root: if the click already landed
    // on the internal trigger, don't double-fire. Guarded: closed shadow
    // root or a missing trigger degrades to the old behavior.
    this.querySelectorAll('.wh-ex__menu-account-login').forEach((pill) => {
      pill.addEventListener('click', (e) => {
        const acc = pill.querySelector('shopify-account');
        const trigger = acc && acc.shadowRoot && acc.shadowRoot.querySelector('button, [role="button"]');
        if (!trigger || e.composedPath().includes(trigger)) return;
        trigger.click();
      });
    });
    if (this.refs.menuButtonMobile) this.refs.menuButtonMobile.addEventListener('click', this.#openMobileNav);
    this.querySelectorAll('[data-wh-chat-open]').forEach((btn) => btn.addEventListener('click', this.#onChatOpen));
    if (this.refs.menuClose) this.refs.menuClose.addEventListener('click', this.#closeMenu);
    if (this.refs.menu) this.refs.menu.addEventListener('click', this.#onMenuClick);
    if (this.refs.menu) this.refs.menu.addEventListener('pointerover', this.#onLinkHover);
    if (this.refs.moreBuyAdd) {
      // The drawer's ADD is a PROXY for the panel's button — one commit
      // path, one state machine (ADD/SOLD OUT/post-add pair behave
      // identically wherever the click happens).
      this.refs.moreBuyAdd.addEventListener('click', () => {
        if (this.refs.panelAdd && !this.refs.panelAdd.disabled) this.refs.panelAdd.click();
      });
    }
    if (this.refs.contentClose) this.refs.contentClose.addEventListener('click', () => this.#closeContent());
    if (this.refs.contentBack) {
      // Mobile nesting: BACK returns one level — to the drawer the view was
      // opened from (product views drawer or the site menu). CLOSE (above)
      // exits the overlay entirely.
      this.refs.contentBack.addEventListener('click', () => {
        this.#closeContent({ keepOverlay: true });
        if (this._contentSource === 'more') {
          this.#showMoreNav();
          // No view is open anymore — un-press the drawer item that opened
          // it (the FAQ button stayed dark after BACK, user bug 2026-08-08).
          if (this.refs.moreList) {
            this.refs.moreList.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
          }
        } else {
          this.#openMenu();
        }
      });
    }
    if (this.refs.contentHistoryBack) {
      // In-canvas history back: shown only when the canvas navigated a level
      // deeper (blog → article, articles list → article) — see #canvasBack.
      this.refs.contentHistoryBack.addEventListener('click', () => this.#canvasBack());
    }
    if (this.refs.contentBody) this.refs.contentBody.addEventListener('click', this.#onContentClick);
    if (this.refs.contentBody) this.refs.contentBody.addEventListener('submit', this.#onContentSubmit);
    if (this.refs.contentBody) this.refs.contentBody.addEventListener('pointerover', this.#onLinkHover);

    // Order matters: the server-rendered route seeds the view first, then a
    // config permalink (more specific — full browse state) may override it,
    // then the live cart hydrates the PICKS asynchronously (cart is truth).
    this.#applyInitialState();
    this.#hydrateFromUrl();
    this.#start();
    this.#hydrateCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('pageshow', this.#onPageShow);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.removeEventListener('keydown', this.#onOverlayKeydown);
    document.removeEventListener('keydown', this.#onSummaryKeydown);
    document.removeEventListener('keydown', this.#onSceneKeydown);
    if (this._dockMq && this._onDockChange) this._dockMq.removeEventListener('change', this._onDockChange);
    if (this._vvGuardApply) {
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', this._vvGuardApply);
      window.removeEventListener('resize', this._vvGuardApply);
    }
    clearInterval(this._chatDockT);
    [
      this._menuHideT, this._mnavHideT, this._moreHideT, this._contentHideT,
      this._addedT1, this._addedT2, this._bannerT, this._shareT,
      this._prefetchNextT, this._prefetchHereT,
    ].forEach(clearTimeout);
    // The only teardown path — a theme-editor section reload (or template
    // swap) disconnects the element; a fresh one reconnects and re-boots.
    this.#disposeScene();
    // A MOVED element (editor reorder) reconnects the SAME node — clear the
    // bind stamp so connectedCallback re-boots instead of bailing dead.
    delete this.dataset.whBound;
  }

  // === Router ===
  // The explorer never reloads. URLs are written as a side effect of state
  // changes (#updateUrl) and read on Back/Forward (#onPopState) and bfcache
  // restores (#onPageShow). Server-rendered entry points hand their route in
  // through the payload's initialState (emitted by the section's Liquid).

  // Seed the view from the URL the server rendered: /products/<handle> opens
  // that product's category with it active (+ explicit ?variant=), /cart
  // opens the summary, everything else stays on the default category. A
  // handle that isn't in the catalog payload falls back to the default
  // category — safety net, not a feature (everything shoppable lives here).
  #applyInitialState() {
    const init = this.data.initialState;
    if (!init) return;
    if (init.route === 'cart') {
      this.state.currentKey = this.summaryKey;
      return;
    }
    if (init.route === 'product' && init.productHandle) {
      const prefCat = new URL(window.location.href).searchParams.get('wh-cat');
      let found = this.#findByHandle(init.productHandle, prefCat);
      if (!found && this.catalogs.length > 1) {
        // The product may live in the other catalog (a museum deep link):
        // install that catalog first, then resolve normally.
        const otherIdx = 1 - this._catalogIdx;
        const inThis = (s, handle) =>
          !!(s && s.products && s.products.some((p) => p.handle === handle));
        const inOther = this.catalogs[otherIdx].steps.some(
          (s) =>
            inThis(s, init.productHandle) ||
            (s && (s.subSteps || []).some((ss) => inThis(ss, init.productHandle)))
        );
        if (inOther) {
          this.#installCatalog(otherIdx);
          found = this.#findByHandle(init.productHandle, prefCat);
        }
      }
      if (!found) {
        console.warn('[wh-explorer] product not in catalog payload:', init.productHandle);
        return;
      }
      this.state.currentKey = found.stepKey;
      if (!this.#nestContextOwner()) {
        this.state.nestOwner = this.#nestOwnerFor(found.stepKey) || this.state.nestOwner;
      }
      const browse = this.state.browse[found.stepKey];
      browse.productId = found.product.id;
      const variant = init.variantId ? this.#variantById(found.product, init.variantId) : null;
      browse.variantId = variant ? variant.id : found.product.defaultVariantId;
      this.state.variantByProduct[found.product.id] = browse.variantId;
      return;
    }
    if (init.route === 'museum') {
      // /pages/museum: land in the museum catalog's first category — no
      // content canvas (the template's old grid stays server-side only).
      // #installCatalog lands on the first category with products.
      if (this.catalogs.length > 1 && this._catalogIdx !== 1) this.#installCatalog(1);
      return;
    }
    if (init.route === 'content') {
      // Article / blog / page URL: the template server-renders the original
      // content sections UNDER the explorer (crawlers read them in the raw
      // HTML); #start lifts them into the canvas once the scene is up.
      this._bootContent = { title: init.contentTitle || '' };
      return;
    }
    if (init.route === 'collection' && init.collectionHandle) {
      // Collection URL → the matching category (key match, or an explicit
      // collection_mapping binding), in either catalog, subs included.
      // No match → default view.
      const matches = (s) =>
        s &&
        (s.key === init.collectionHandle ||
          (Array.isArray(s.collectionHandles) && s.collectionHandles.includes(init.collectionHandle)));
      for (let c = 0; c < this.catalogs.length; c++) {
        let step = null;
        for (const s of this.catalogs[c].steps || []) {
          if (matches(s)) {
            step = s;
            break;
          }
          const sub = (s.subSteps || []).find(matches);
          if (sub) {
            step = sub;
            break;
          }
        }
        if (step) {
          if (c !== this._catalogIdx) this.#installCatalog(c);
          this.state.currentKey = step.key;
          return;
        }
      }
      console.warn('[wh-explorer] no category matches collection:', init.collectionHandle);
      return;
    }
    if (init.route === 'campaign' && init.campaignPath) {
      // Campaign entry URL: resolve which product owns this campaign and let
      // #start open the full campaign experience (views drawer + canvas).
      const found = this.#findByCampaignPath(init.campaignPath);
      // The category BEHIND the canvas must be steered here, before first
      // render — otherwise the default first category renders underneath and
      // closing the canvas strands the visitor there (Kojima deep link showed
      // keyboards behind the campaign — user 2026-09-04). The entry's explicit
      // `explorer_category` wins; otherwise the category holding the first
      // product bound to this campaign via custom.campaign_page.
      let target = init.campaignCategory ? this.#findStepAnywhere(init.campaignCategory) : null;
      if (!target && found) target = { catalogIdx: found.catalogIdx, key: found.stepKey };
      let own = null;
      if (target) {
        if (target.catalogIdx !== this._catalogIdx) this.#installCatalog(target.catalogIdx);
        const step = this.categories.get(target.key);
        // Market pruning may have dropped the step (#installCatalog) — then
        // the default view stays.
        if (step && step.key !== this.summaryKey) {
          this.state.currentKey = step.key;
          if (!this.#nestContextOwner()) {
            this.state.nestOwner = this.#nestOwnerFor(step.key) || this.state.nestOwner;
          }
          // Active product: a campaign-bound product living IN this category,
          // so closing the canvas lands on the featured product (a campaign
          // shared by several colours lands on the first bound one).
          const path = this.#campaignPathKey(init.campaignPath);
          own = step.products.find((p) => p.campaignUrl && this.#campaignPathKey(p.campaignUrl) === path) || null;
          const browse = this.state.browse[step.key];
          if (own && browse) {
            browse.productId = own.id;
            browse.variantId = own.defaultVariantId;
          }
        }
      }
      if (own || found) {
        this._bootCampaign = own || found.product;
      } else {
        // Entry not bound to any catalog product — show it as plain content.
        this._bootContent = { title: init.contentTitle || '' };
      }
      return;
    }
    if (init.route === 'home') {
      // Homepage landing (user spec 2026-08-07, "every hard load of /"):
      // the merchant's global_values entry names a URL to open over the
      // explorer. The value arrives already translated for the request's
      // language (metaobject field). Resolved HERE so a campaign landing
      // also steers the scene to the OWNING product's step before first
      // render — closing the canvas then lands the visitor on that product.
      // Skipped in the theme editor so merchants can edit the explorer.
      const landing = typeof this.data.loadout.landingUrl === 'string' ? this.data.loadout.landingUrl.trim() : '';
      if (!landing || (window.Shopify && window.Shopify.designMode)) return;
      let pathname = landing;
      try {
        pathname = new URL(landing, window.location.href).pathname;
      } catch (_) {
        /* keep raw value — #findByCampaignPath strips queries itself */
      }
      const campaign = this.#findByCampaignPath(pathname);
      if (campaign) {
        if (campaign.catalogIdx !== this._catalogIdx) this.#installCatalog(campaign.catalogIdx);
        const found = this.#findByHandle(campaign.product.handle);
        if (found) {
          this.state.currentKey = found.stepKey;
          this.state.browse[found.stepKey].productId = campaign.product.id;
        }
        this._bootCampaign = campaign.product;
        // Landing boots FROM `/`, so unlike a campaign deep link the URL
        // must be pushed — Back returns to `/` and closes the canvas.
        this._bootLandingPush = true;
      } else {
        this._bootLanding = landing;
      }
    }
  }

  // `preferKey` (from ?wh-cat=) breaks ties when the same product lives in
  // several categories: without it the FIRST step containing the handle wins,
  // which on reload silently moved a keyboard-accessories visitor back to the
  // main accessories category (user 2026-08-19).
  #findByHandle(handle, preferKey = null) {
    const steps = this.realSteps.concat(this.subSteps || []);
    if (preferKey) {
      const pref = steps.find((s) => s.key === preferKey);
      const product = pref && pref.products.find((p) => p.handle === handle);
      if (product) return { stepKey: pref.key, product };
    }
    for (const step of steps) {
      const product = step.products.find((p) => p.handle === handle);
      if (product) return { stepKey: step.key, product };
    }
    return null;
  }

  // Which catalog (shop 0 / museum 1) holds a product handle, main + sub
  // categories, or -1. The router installs that catalog before resolving the
  // product, so shop ⇄ museum product links and Back/Forward across the two
  // catalogs steer the scene instead of falling into the content canvas.
  #catalogIndexForHandle(handle) {
    const has = (s) => !!(s && s.products && s.products.some((p) => p.handle === handle));
    for (let c = 0; c < this.catalogs.length; c++) {
      if ((this.catalogs[c].steps || []).some((s) => has(s) || (s.subSteps || []).some(has))) return c;
    }
    return -1;
  }

  // Locale-normalize a campaign path — links and serialized campaignUrls may
  // carry different market prefixes (/en-eu/pages/… vs /pages/…).
  #campaignPathKey(u) {
    return this.#localPath((u || '').split('?')[0]);
  }

  // Find the product (in any catalog, sub-categories included) whose campaign
  // entry lives at `path`. Sub-categories were skipped until 2026-09-04 — a
  // campaign bound to a "Related products" item never resolved.
  #findByCampaignPath(path) {
    const target = this.#campaignPathKey(path);
    for (let c = 0; c < this.catalogs.length; c++) {
      for (const step of this.catalogs[c].steps) {
        for (const s of [step, ...(step.subSteps || [])]) {
          const product = (s.products || []).find(
            (p) => p.campaignUrl && this.#campaignPathKey(p.campaignUrl) === target
          );
          if (product) return { catalogIdx: c, stepKey: s.key, product };
        }
      }
    }
    return null;
  }

  // Locate a category key (main or sub) across the RAW catalogs — boot-time
  // lookup, before/independent of which catalog is installed.
  #findStepAnywhere(key) {
    if (!key) return null;
    for (let c = 0; c < this.catalogs.length; c++) {
      for (const step of this.catalogs[c].steps) {
        for (const s of [step, ...(step.subSteps || [])]) {
          if (s && s.key === key) return { catalogIdx: c, key: s.key };
        }
      }
    }
    return null;
  }

  // The canonical path for the current view: /cart on the summary, the active
  // product's own URL (locale-prefixed — product.url) otherwise.
  #routePathFor(stepKey) {
    if (this.#isSummary(stepKey)) {
      return (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';
    }
    // Every museum view lives at the museum page (its dedicated URL);
    // museum product deep links still resolve INBOUND via #applyInitialState.
    if (this._catalogIdx === 1) return this.#museumPath();
    const browse = this.state.browse[stepKey];
    const product = browse ? this.#productById(stepKey, browse.productId) : null;
    return product && product.url ? product.url.split('?')[0] : null;
  }

  // Localised museum page path: keep whatever locale prefix the visitor is on
  // (/en-eu/pages/museum), the same prefix #localPath strips.
  #museumPath() {
    const m = window.location.pathname.match(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i);
    return (m ? m[0] : '') + MUSEUM_PAGE;
  }

  #onPopState = () => {
    // Consume the entry: owned paths steer the scene (canvas closes if open);
    // any other path re-opens the content canvas for that URL — usually a
    // cache hit, so Back/Forward between articles is instant. History is
    // never written from here.
    if (this.routeMode !== 'full') return;
    const path = window.location.pathname;
    if (this.#ownsPath(path)) {
      if (this._contentOpen) this.#closeContent({ updateUrl: false });
      this.#applyLocation();
      return;
    }
    const campaign = this.#findByCampaignPath(path);
    if (campaign) {
      this.#openCampaign(campaign.product, { push: false });
    } else {
      this.#openContent(window.location.href, '', { push: false });
    }
  };

  #onPageShow = (e) => {
    // bfcache restore (Back from checkout is the big one): re-apply the route
    // AND re-sync picks from the live cart — checkout may have changed it.
    if (e && e.persisted) {
      this.#applyLocation();
      this.#hydrateCart();
    }
  };

  // Steer the explorer to whatever the location says, WITHOUT writing history
  // (we're consuming an entry, not creating one — #renderStep alone never
  // touches the URL). Back/Forward between two products in the same category
  // re-renders in place; category/summary changes get the usual veil.
  #applyLocation() {
    if (this.routeMode !== 'full') return;
    const path = this.#localPath(window.location.pathname);
    let targetKey = null;
    // Catalog follows the path: the museum page installs the museum, home
    // (and any other owned path) is the shop. #installCatalog restores that
    // catalog's cached browse state, so this is a no-op when already there.
    if (path === MUSEUM_PAGE && this.catalogs.length > 1) {
      if (this._catalogIdx !== 1) this.#installCatalog(1);
    } else if (path === '/' && this._catalogIdx !== 0) {
      this.#installCatalog(0);
    }
    const cm = path.match(/^\/collections\/([^/?#]+)$/);
    const cmKey = cm ? this.#collectionStepKey(decodeURIComponent(cm[1])) : null;
    if (path === '/cart') {
      targetKey = this.summaryKey;
    } else if (cmKey) {
      targetKey = cmKey; // key match or explicit collection_mapping binding
    } else {
      const m = path.match(/\/products\/([^/?#]+)/);
      if (m) {
        const prefCat = new URL(window.location.href).searchParams.get('wh-cat');
        const handle = decodeURIComponent(m[1]);
        const cIdx = this.#catalogIndexForHandle(handle);
        if (cIdx >= 0 && cIdx !== this._catalogIdx) this.#installCatalog(cIdx);
        const found = this.#findByHandle(handle, prefCat);
        if (found) {
          targetKey = found.stepKey;
          const browse = this.state.browse[found.stepKey];
          browse.productId = found.product.id;
          const variantParam = new URL(window.location.href).searchParams.get('variant');
          const variant = variantParam ? this.#variantById(found.product, Number(variantParam)) : null;
          browse.variantId = variant
            ? variant.id
            : this.state.variantByProduct[found.product.id] || found.product.defaultVariantId;
          this.state.variantByProduct[found.product.id] = browse.variantId;
        }
      }
    }
    const isHome = !targetKey; // '/' (or anything unrecognised) → default category
    if (isHome) targetKey = this.stepKeys[0] ?? this.summaryKey;
    // Re-seed the nest context for the TARGET step (see #nestOwnerFor) — the
    // stored owner survives only if its journey chain already covers the
    // target (currentKey is still the OLD step here, so #nestContextOwner
    // would validate against the wrong key).
    if (targetKey !== this.summaryKey) {
      const owner = this.state.nestOwner ? this.categories.get(this.state.nestOwner) : null;
      const chainOk =
        owner && Array.isArray(owner.journey) && [owner.key, ...owner.journey].includes(targetKey);
      if (!chainOk) this.state.nestOwner = this.#nestOwnerFor(targetKey) || null;
    }
    const apply = () => {
      this.#renderStep(targetKey);
      // Re-sync the title to the restored view (renderStep alone never
      // touches the URL or title — that's #updateUrl's job on forward nav).
      // Home restores the server-rendered title, not the active product's.
      const browse = this.state.browse[targetKey];
      const product =
        !isHome && browse && !this.#isSummary(targetKey)
          ? this.#productById(targetKey, browse.productId)
          : null;
      this.#syncTitle(product);
    };
    if (targetKey === this.state.currentKey) {
      apply();
      return;
    }
    const rune = this.#isSummary(targetKey) || this.#isSummary(this.state.currentKey);
    this.#transition({ rune, hold: rune ? VEIL_SUMMARY_HOLD_MS : VEIL_SCENE_HOLD_MS }, apply);
  }

  // === Site-nav drawer (MENU) + content canvas ===
  // The MENU is a right glass drawer (shared side-modal language with the
  // specs drawer). Menu links resolve three ways: in-catalog links (products,
  // /cart, home) route the live scene; every OTHER same-origin page (contact,
  // knowledge, campaign metaobject pages, …) is fetched and rendered in the
  // content canvas beside the drawer — closing it lands back in the explorer;
  // off-origin links navigate normally. On mobile the canvas is a nested
  // fullscreen sheet with a back-to-menu button.

  #openMenu = () => {
    // Phones: the site nav lives in the LEFT drawer (docked below 750px) —
    // the right aside is an empty shell there, so route every menu open to
    // the mobile drawer's SITE tab instead.
    if (window.matchMedia('(max-width: 749px)').matches && this.refs.mobileNav) {
      this.#setMobileNavTab('site');
      this.#openMobileNav();
      return;
    }
    const menu = this.refs.menu;
    if (!menu) return;
    // One right drawer at a time — the menu and the views drawer share the slot.
    this.#closeMoreNav();
    if (!menu.classList.contains('is-open')) {
      this._menuReturnFocus = /** @type {HTMLElement|null} */ (document.activeElement);
    }
    clearTimeout(this._menuHideT);
    menu.hidden = false;
    void menu.offsetWidth; // reflow so the slide transition runs same-frame
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    if (this.refs.menuButton) this.refs.menuButton.setAttribute('aria-expanded', 'true');
    if (this.refs.menuButtonMobile) this.refs.menuButtonMobile.setAttribute('aria-expanded', 'true');
    this.#syncOverlayKeydown();
    if (this.refs.menuClose && typeof this.refs.menuClose.focus === 'function') {
      this.refs.menuClose.focus();
    }
  };

  #closeMenu = () => {
    this.#closeMobileNav();
    const menu = this.refs.menu;
    if (!menu) return;
    // Closing the MENU closes the canvas with it (user 2026-08-18, second
    // report — widened from menu-sourced only: 'route'-sourced canvases,
    // e.g. a deep-linked page the visitor then opened the menu over, close
    // too). Only 'more'-sourced product views survive — they belong to the
    // see-more drawer, not the menu. keepOverlay avoids recursion.
    if (this._contentOpen && this._contentSource !== 'more') {
      this.#closeContent({ keepOverlay: true });
    }
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    if (this.refs.menuButton) this.refs.menuButton.setAttribute('aria-expanded', 'false');
    if (this.refs.menuButtonMobile) this.refs.menuButtonMobile.setAttribute('aria-expanded', 'false');
    clearTimeout(this._menuHideT);
    this._menuHideT = setTimeout(() => {
      menu.hidden = true;
    }, 320); // slide transition + margin — keep in sync with the CSS
    this.#syncOverlayKeydown();
    if (this._menuReturnFocus && typeof this._menuReturnFocus.focus === 'function') {
      this._menuReturnFocus.focus();
    }
    this._menuReturnFocus = null;
  };

  // Escape stays armed only while SOMETHING is open. Every open/close path
  // calls this instead of adding/removing by hand — the old per-path
  // removals left Escape armed after the mobile/see-more drawers closed.
  // Leaving the summary = the review panel's "close": back to the last
  // category. Shared by the close button, the wordmark (summary only) and
  // Escape (user 2026-08-18).
  // Notify-me dialog: fill the per-product tag, then the native modal.
  // Same customer-form pattern as the K-001 popup; the post redirect
  // reopens it in its success state (data-wh-posted).
  // `custom` (campaign CTAs, user 2026-09-10): { heading, sub, tag } from the
  // button's data-wh-notify-* attributes override the category copy and
  // replace the per-product tag (the campaign's product is not always in
  // the loadout, so there is no product to derive it from).
  #openNotifyDialog(product, variant, custom = null) {
    const d = this.refs.notifyDialog;
    if (!d || d.open) return;
    // Per-category copy override (Wallhack 2026-08-19: the popup should
    // sell the upside, e.g. "join the waiting list for the next keyboard
    // restock") — category metaobject fields, translatable per market.
    const step = this.categories.get(this.state.currentKey);
    if (this.refs.notifyHeading) {
      this.refs.notifyHeading.textContent = (custom && custom.heading) || (step && step.notifyHeading) || 'GET NOTIFIED';
    }
    if (this.refs.notifySub) {
      this.refs.notifySub.textContent =
        (custom && custom.sub) || (step && step.notifyText) || 'Sign up and be the first to know when it\'s back in stock.';
    }
    if (this.refs.notifyTags && (product || (custom && custom.tag))) {
      // Base value carries the market (Liquid-rendered); append the product.
      const base = this.refs.notifyTags.dataset.base || this.refs.notifyTags.value || 'notify-me';
      this.refs.notifyTags.dataset.base = base;
      let tags = base + ',' + (custom && custom.tag ? custom.tag : 'notify-' + (product.handle || product.id));
      // Category source identifier (user 2026-08-19): tag source-<value>;
      // a Shopify Flow copies it onto the customer's source METAFIELD
      // (storefront forms cannot write metafields directly).
      if (!custom && step && step.notifySource) tags += ',source-' + step.notifySource;
      this.refs.notifyTags.value = tags;
    }
    d.showModal();
  }

  #closeSummary() {
    if (!this.#isSummary(this.state.currentKey)) return;
    this.#goToStep(this.stepKeys[this.stepKeys.length - 1]);
  }

  #anyOverlayOpen() {
    return (
      this._contentOpen ||
      ['menu', 'mobileNav', 'moreNav'].some(
        (k) => this.refs[k] && this.refs[k].classList.contains('is-open')
      )
    );
  }

  // Always-armed (unlike the overlay keydown): Escape on the bare summary
  // scene leaves it (user 2026-08-18). Overlays win — their own handler
  // peels them first while anything is open.
  #onSummaryKeydown = (e) => {
    if (e.key !== 'Escape' || this.#anyOverlayOpen()) return;
    this.#closeSummary();
  };

  // Scene keyboard shortcuts (user 2026-08-20). Always armed; every branch
  // is inert while a form control has focus.
  // • Backquote (the physical key above Tab — the CS console key; layout-
  //   independent, so ` and § alike): opens the TERMINAL in a new tab —
  //   same destination as the topbar pill, and silent when the merchant
  //   hasn't configured that link.
  // • Left/Right arrows: page the rail — same path as the pager buttons and
  //   a 3D pad click, wrap-around included. Additionally inert while any
  //   overlay/dialog is open, on the summary, and on single-product steps.
  #onSceneKeydown = (e) => {
    if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return;
    const t = e.target;
    if (t && t.closest && t.closest('input, textarea, select, [contenteditable="true"]')) return;
    // The console key = the physical key above Tab, across as many boards
    // as possible (user 2026-08-20). Three nets, most specific first:
    // • code Backquote — the physical position on ANSI/ISO/Windows-JIS
    //   (layout-independent, dead keys included: German ^, French ², …).
    // • code IntlBackslash — Chromium on ISO (European) Macs SWAPS the
    //   above-Tab key's code to this (the notorious macOS ISO quirk;
    //   user's Danish Mac hit it).
    // • key characters/names — belt-and-braces for stacks that misreport
    //   codes: the characters that key prints on common layouts (` ~ § ±
    //   ½ ² º °) and the JIS 半角/全角 key names. Mac JIS boards have no
    //   such key at all — those users have the topbar TERMINAL pill.
    {
      const CONSOLE_KEYS = ['`', '~', '§', '±', '½', '²', 'º', '°', 'Zenkaku', 'Hankaku', 'ZenkakuHankaku'];
      if (e.code === 'Backquote' || e.code === 'IntlBackslash' || CONSOLE_KEYS.includes(e.key)) {
        const link = this.querySelector('.wh-ex__terminal');
        if (link && link.href) {
          e.preventDefault();
          window.open(link.href, '_blank', 'noopener');
        }
        return;
      }
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (this.#anyOverlayOpen() || document.querySelector('dialog[open]')) return;
    const stepKey = this.state.currentKey;
    if (this.#isSummary(stepKey)) return;
    const step = this.categories.get(stepKey);
    const browse = this.state.browse[stepKey];
    const rail = this.#railProducts(step);
    if (!step || !browse || rail.length < 2) return;
    const idx = rail.findIndex((p) => p.id === browse.productId);
    if (idx === -1) return;
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = rail[(idx + dir + rail.length) % rail.length];
    if (next) this.#setActiveProduct(next.id);
  };

  #syncOverlayKeydown() {
    const open = this.#anyOverlayOpen();
    if (open) document.addEventListener('keydown', this.#onOverlayKeydown);
    else document.removeEventListener('keydown', this.#onOverlayKeydown);
  }

  // Escape peels overlays innermost-first: content sheet → drawer → explorer.
  #onOverlayKeydown = (e) => {
    if (e.key !== 'Escape') return;
    if (this._contentOpen) {
      this.#closeContent();
      return;
    }
    if (this.refs.moreNav && this.refs.moreNav.classList.contains('is-open')) {
      this.#closeMoreNav();
      return;
    }
    this.#closeMenu(); // also funnels the mobile left drawer closed
  };

  // === Campaign pages ===
  // A campaign page is an [NB26] Drops metaobject entry bound to a product
  // via custom.campaign_page — one architecture across all products. It is
  // just another product view: the canvas shows the campaign entry beside the
  // product views drawer (the old campaign switcher drawer is retired —
  // navigation between items is explorer-only).

  // `fromDom` (2026-09-11): a campaign DEEP LINK already has the campaign
  // server-rendered in #MainContent. Lift those sections (like a content
  // deep link) instead of fetching the same URL again — the fetch path left
  // the server copy sitting under the explorer, where it bled through on
  // phones (M-001: heading and tagline visible around the canvas) and
  // doubled every image request. Homepage landings boot from `/` (the
  // homepage is in #MainContent, not the campaign) and keep fetching.
  #openCampaign(product, { push = true, fromDom = false } = {}) {
    if (!product || !product.campaignUrl) return;
    this.#closeMenu();
    // Deep links / Back-Forward land here without the views drawer open —
    // build it for the owning product so the campaign has its navigation.
    if (!this.refs.moreNav || !this.refs.moreNav.classList.contains('is-open')) {
      this.#openMoreNav(product);
    }
    this.#markMoreActive(this.labels.moreAbout);
    this._contentSource = 'more';
    this.#applyTint(product.campaignTint || null);
    // Front-load the campaign title from the payload — the canvas heading is
    // final from the first frame (no product-title → campaign-title flip
    // while the fetch runs).
    const title = product.campaignTitle || this.#displayTitleFor(product);
    if (fromDom && this.#mainContentSections().length) {
      this.#openContentFromDom(title);
      this._contentSource = 'more'; // the views drawer owns BACK, as for a fetched campaign
      return;
    }
    this.#openContent(product.campaignUrl, title, { campaign: true, push });
  }

  // The page's own server-rendered content sections still sitting in
  // #MainContent (everything but the explorer's own section).
  #mainContentSections() {
    const main = document.getElementById('MainContent');
    if (!main) return [];
    const ownSection = this.closest('.shopify-section');
    return Array.from(main.children).filter((el) => el !== ownSection && !el.querySelector('wh-explorer'));
  }

  // The catalog button keeps ONE width — that of whichever label pair
  // (shop/museum) is longest — so toggling catalogs never shifts the topbar
  // (user spec 2026-08-01). Measured once by rendering both pairs offscreen
  // via the live element, then pinned as min-width.
  #sizeCatalogButton() {
    if (this._catalogBtnSized) return;
    const btn = this.refs.catalogSwitch;
    const label = this.refs.catalogSwitchLabel;
    if (!btn || !label || btn.hidden) return;
    // Label only — the hint sub-line was removed in the Frame 456 redesign.
    const current = label.textContent;
    let max = 0;
    [this.labels.shopButton || 'SHOP', this.labels.museumButton || 'MUSEUM'].forEach((l) => {
      label.textContent = l;
      max = Math.max(max, btn.offsetWidth);
    });
    label.textContent = current;
    // A zero measure means the button wasn't laid out (display:none state,
    // e.g. summary) — leave the once-flag unset so a later render retries.
    if (!max) return;
    this._catalogBtnSized = true;
    btn.style.minWidth = `${Math.ceil(max)}px`;
  }

  // Scene background image (product > category > none). Two absolutely
  // stacked layers crossfade: the incoming URL paints on the idle layer
  // (preloaded first so the fade never reveals a half-loaded image), then
  // opacities swap. null fades both out, leaving the scene color.
  #applySceneBg(url) {
    const a = this.refs.sceneBgA;
    const b = this.refs.sceneBgB;
    if (!a || !b) return;
    if (url === this._sceneBgCurrent) return;
    this._sceneBgCurrent = url;
    const showOn = (layer, other) => {
      layer.style.backgroundImage = `url("${url}")`;
      layer.classList.add('is-visible');
      other.classList.remove('is-visible');
    };
    if (!url) {
      a.classList.remove('is-visible');
      b.classList.remove('is-visible');
      return;
    }
    const incoming = this._sceneBgFlip ? a : b;
    const outgoing = this._sceneBgFlip ? b : a;
    this._sceneBgFlip = !this._sceneBgFlip;
    const pre = new Image();
    const token = url;
    pre.onload = () => {
      if (this._sceneBgCurrent === token) showOn(incoming, outgoing);
    };
    pre.onerror = () => {
      if (this._sceneBgCurrent === token) {
        // Unloadable image: fall back to the plain scene color.
        incoming.classList.remove('is-visible');
        outgoing.classList.remove('is-visible');
      }
    };
    pre.src = url;
  }

  // Campaign color_tint (metaobject field, optional): a subtle wash mixed
  // into every glass surface (menu, campaign drawer, content canvas) while
  // that campaign is open. Unset → the variable is absent and the tint layer
  // in .wh-ex-glass resolves to transparent — exactly the untinted look.
  #applyTint(color) {
    if (color) {
      this.style.setProperty('--wh-ex-tint-veil', `color-mix(in srgb, ${color} 20%, transparent)`);
    } else {
      this.style.removeProperty('--wh-ex-tint-veil');
    }
  }

  // Re-show the already-built views drawer (mobile BACK from a canvas view —
  // the list for the active product is still in the DOM).
  #showMoreNav() {
    const nav = this.refs.moreNav;
    if (!nav) return;
    clearTimeout(this._moreHideT);
    nav.hidden = false;
    void nav.offsetWidth;
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    this.#syncOverlayKeydown();
  }

  #markMoreActive(label) {
    const list = this.refs.moreList;
    if (!list) return;
    list.querySelectorAll('.wh-ex__campaign-item').forEach((el) => {
      el.classList.toggle('is-active', el.textContent === label);
    });
  }

  #onMenuClick = (e) => {
    this.#interceptLink(e, { closeMenuOnRoute: true });
  };

  // SmartBot opener (the mobile bar's chat icon — see the section's mbar
  // markup): SmartBot's documented command queue. The array push is safe
  // before the SDK loads (it processes the queue at init) and a no-op only
  // if the app embed is missing entirely.
  #onChatOpen = () => {
    (window.SmartBotEventLayer = window.SmartBotEventLayer || []).push(['event', 'open_chat']);
  };

  // Links INSIDE canvas content get the same treatment, so navigation stays
  // in the canvas (blog index → post, museum card → campaign, …).
  #onContentClick = (e) => {
    // Canvas content may carry a [data-wh-canvas-close] control (campaign
    // CTAs like Kojima's "see products", user 2026-09-04): the products sit
    // in the explorer BEHIND the page, so the CTA simply closes the canvas.
    // Checked before link interception — it's a <button>, not an <a>.
    const closer = e.target && e.target.closest && e.target.closest('[data-wh-canvas-close]');
    if (closer) {
      e.preventDefault();
      this.#closeContent();
      return;
    }
    // [data-wh-notify-open] (campaign "get notified" CTAs, user 2026-09-10):
    // opens the explorer's own sign-up dialog over the canvas, with the
    // button's data-wh-notify-heading / -sub / -tag as the copy and tag.
    const notifier = e.target && e.target.closest && e.target.closest('[data-wh-notify-open]');
    if (notifier) {
      e.preventDefault();
      this.#openNotifyDialog(null, null, {
        heading: notifier.dataset.whNotifyHeading,
        sub: notifier.dataset.whNotifySub,
        tag: notifier.dataset.whNotifyTag,
      });
      return;
    }
    this.#interceptLink(e, { closeMenuOnRoute: true });
  };

  // [WH] Page lock, seamless unlock (user 2026-09-10). The lock screen's form
  // (snippets/wh-lock.liquid, [data-wh-lock-form]) would POST to /cart/update
  // and reload the page. In the canvas we do the same two steps in the
  // background: store the passcode as the cart attribute via /cart/update.js,
  // then re-fetch THIS page (cache dropped) and re-render it in place. The
  // server decides: still locked → the re-render carries the wrong-passcode
  // message; right → the real page. The page content is never in the HTML
  // before the passcode is right, and a failed request falls back to the
  // plain form submit so nothing depends on this handler.
  #onContentSubmit = async (e) => {
    const form = e.target && e.target.closest && e.target.closest('[data-wh-lock-form]');
    if (!form || form.dataset.whBusy) return;
    const input = form.querySelector('input[type="password"]');
    const m = input && /^attributes\[(.+)\]$/.exec(input.name || '');
    if (!m) return; // not our contract — let the browser post it
    e.preventDefault();
    form.dataset.whBusy = '1';
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [m[1]]: input.value } }),
      });
      if (!res.ok) throw new Error('cart/update ' + res.status);
      const url = this._contentUrl || window.location.pathname + window.location.search;
      // Drop every cached copy of this page — the cache key is whatever string
      // opened it (absolute or path), so clear both spellings.
      for (const key of Array.from(this._contentCache.keys())) {
        try {
          if (new URL(key, window.location.href).pathname === new URL(url, window.location.href).pathname) {
            this._contentCache.delete(key);
          }
        } catch (_) {
          /* unparsable cache key — ignore */
        }
      }
      const title = this.refs.contentTitle ? this.refs.contentTitle.textContent : '';
      await this.#openContent(url, title, { push: false, reload: true });
      // Still locked (wrong passcode): put the cursor back in the field.
      const again = this.refs.contentBody && this.refs.contentBody.querySelector('[data-wh-lock-form] input[type="password"]');
      if (again) again.focus();
    } catch (err) {
      console.warn('[wh-explorer] lock unlock failed, falling back to a full submit', err);
      delete form.dataset.whBusy;
      if (button) button.disabled = false;
      form.submit();
    }
  };

  // Prefetch-on-hover (user item 7, 2026-08-07): the pointerover twin of
  // #interceptLink, bound wherever it is bound — any link that WOULD open in
  // the canvas gets its fetch started on hover, so the click is usually a
  // cache hit. Mirrors the click routing decisions exactly: steered paths
  // and collections navigate without a fetch (nothing to warm), campaign
  // links warm their canonical campaignUrl (the key #openCampaign uses).
  // Delegated, so links added by future features are covered automatically.
  #onLinkHover = (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!a) return;
    let url;
    try {
      url = new URL(a.href, window.location.href);
    } catch (_) {
      return;
    }
    if (url.origin !== window.location.origin) {
      // External links always open a NEW TAB (user 2026-08-18).
      a.target = '_blank';
      a.rel = 'noopener';
      return;
    }
    if (url.pathname === window.location.pathname && url.hash) return;
    if (this.routeMode === 'full' && this.#ownsPath(url.pathname)) return;
    if (/^\/collections\/[^/?#]+$/.test(this.#localPath(url.pathname))) return;
    if (this.#isAccountPath(url.pathname)) return; // hard nav — no prefetch
    if (this.#isAppProxyPath(url.pathname)) return; // hard nav — no prefetch
    const campaign = this.#findByCampaignPath(url.pathname);
    this.#prefetchContent(campaign ? campaign.product.campaignUrl : url.toString());
  };

  // Customer-account URLs can never render in the canvas (hosted login /
  // account pages own their flow — new customer accounts redirect off-origin
  // anyway when the path is same-origin). Browser-default navigation.
  // App-PROXY pages (/apps/…, /tools/…, /a/…) are third-party apps rendered
  // server-side through Shopify's proxy — AfterShip's returns portal is a
  // whole Next.js app (its own bundles + __NEXT_DATA__, none of it inside a
  // .shopify-app-block wrapper). Adopting that markup can never work: the
  // hydration bundles are cross-origin defer scripts that already missed
  // their document, and re-running a framework boot against transplanted
  // DOM is not a thing to attempt. A real navigation renders the page
  // server-side WITH its scripts, and the explorer's own boot lifts the
  // hydrated app into the canvas (#openContentFromDom) — which is exactly
  // why these pages already work "on refresh" (Philipp 2026-08-31, same
  // class as the Hulk-forms report). Browser-default navigation.
  #isAppProxyPath(pathname) {
    return /^\/(apps|tools|a)(\/|$|\?)/.test(this.#localPath(pathname));
  }

  #isAccountPath(pathname) {
    return /^\/(account|customer_account|customer_authentication|customer_identity)(\/|$|\?)/.test(this.#localPath(pathname));
  }

  // Shared link resolution for the menu drawer and canvas content:
  //   in-catalog paths        → steer the live scene (no reload)
  //   a product's campaign    → the full campaign experience
  //   other same-origin pages → render in the canvas
  //   off-origin / same-page anchors → browser default
  #interceptLink(e, { closeMenuOnRoute = false } = {}) {
    const a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!a) return;
    // The article back bar (wh-article-back section) "knows where it came
    // from" (user 2026-08-12): with a canvas back level on the stack (blog →
    // article OR product articles list → article) it walks BACK instead of
    // navigating to its fallback blog link — so it returns to the product's
    // articles view when that's where the visitor came from.
    if (a.classList.contains('wh-ab__btn') && this._canvasStack && this._canvasStack.length) {
      e.preventDefault();
      this.#canvasBack();
      return;
    }
    let url;
    try {
      url = new URL(a.href, window.location.href);
    } catch (_) {
      return;
    }
    if (url.origin !== window.location.origin) {
      // External links always open a NEW TAB (user 2026-08-18).
      a.target = '_blank';
      a.rel = 'noopener';
      return;
    }
    if (url.pathname === window.location.pathname && url.hash) return; // in-page anchor
    if (this.routeMode === 'full' && this.#ownsPath(url.pathname)) {
      e.preventDefault();
      this.#closeContent({ updateUrl: false }); // we push the owned URL ourselves next
      if (closeMenuOnRoute) this.#closeMenu();
      this._routeArmed = true; // user-driven navigation
      window.history.pushState({ whEx: true }, '', url.toString());
      this.#applyLocation();
      return;
    }
    const campaign = this.#findByCampaignPath(url.pathname);
    if (campaign) {
      e.preventDefault();
      this.#closeMenu();
      this.#openCampaign(campaign.product);
      return;
    }
    if (this.#isAccountPath(url.pathname)) {
      return; // no preventDefault → hosted account/login flow navigates normally
    }
    if (this.#isAppProxyPath(url.pathname)) {
      // Embedded third-party apps (returns portal etc.) open in a NEW TAB
      // (user 2026-09-08): they can't live in the canvas (see
      // #isAppProxyPath), and a same-tab navigation would silently drop the
      // visitor out of the explorer with no way back. Same treatment as an
      // external link; no preventDefault, so the browser opens the tab.
      a.target = '_blank';
      a.rel = 'noopener';
      return;
    }
    // Collections are NEVER canvas content (user decision 2026-08-05): a
    // mapped/matched handle steered above via #ownsPath; an UNMATCHED one
    // gets a normal navigation — the cleanup policy 301s retired collections
    // to mapped ones, so the server redirect lands on a page that boots
    // steered. (/collections without a handle stays a content page.)
    if (/^\/collections\/[^/?#]+$/.test(this.#localPath(url.pathname))) {
      return; // no preventDefault → full navigation
    }
    e.preventDefault();
    this.#openContent(url.toString(), (a.textContent || '').trim());
  }

  // Does the explorer own this path? Home, the cart route, and any catalog
  // product URL. Non-catalog products return false → they render in the
  // content canvas like any other page.
  // Market/locale prefixes (/en-eu/…) must not defeat route matching: strip
  // the current root prefix AND any generic locale prefix — RTE-authored
  // links carry hardcoded prefixes from other markets than the one being
  // browsed (e.g. an /en-eu/… link inside an article served at /).
  #localPath(path) {
    const root = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.root_url) || '/';
    if (root !== '/' && path.startsWith(root)) {
      const stripped = path.slice(root.length);
      path = stripped.startsWith('/') ? stripped : '/' + stripped;
    }
    // Shopify locale prefixes are IETF-ish: /xx or /xx-yy, either followed by
    // a path or standing alone (locale home).
    return path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
  }

  #ownsPath(rawPath) {
    const path = this.#localPath(rawPath);
    if (path === '/') return true;
    if (path === '/cart') return true;
    if (path === MUSEUM_PAGE) return this.catalogs.length > 1; // the museum page IS the museum catalog
    // Collections ARE the category views: a collection whose handle matches a
    // category step key OR is bound via a category's collection_mapping
    // steers the scene (same as its deep link). Unmatched collections
    // (/collections, other catalogs) render in the canvas.
    const cm = path.match(/^\/collections\/([^/?#]+)$/);
    if (cm) return !!this.#collectionStepKey(decodeURIComponent(cm[1]));
    const m = path.match(/\/products\/([^/?#]+)/);
    return !!(m && this.#catalogIndexForHandle(decodeURIComponent(m[1])) >= 0);
  }

  // Fetch a same-origin page and show its #MainContent in the canvas.
  // Scripts are stripped (campaign/content pages must be static content);
  // stylesheet links the fetched page needs are hoisted into our head once.
  // Cached per URL (bypassed in the theme editor so merchants see edits).
  // `reload` (page lock, 2026-09-10): re-render the SAME canvas URL after the
  // visitor's passcode was stored — no back-stack, history, source or tint
  // changes; the title is kept. Caller has already dropped the cache entry.
  async #openContent(url, title, { campaign = false, push = true, source = null, reload = false } = {}) {
    const content = this.refs.content;
    const body = this.refs.contentBody;
    if (!content || !body) return;
    // Canvas back stack — HARD RULE (user, 2026-08-05): the in-canvas back
    // chevron exists ONLY for articles. An article opened from the overall
    // blog (or a tagged list) goes back to the blog; an article opened from
    // the product's articles list goes back to the list. Every other canvas
    // page is one level deep by design, so nothing else ever stacks a back
    // level (an article link inside e.g. an FAQ page opens WITHOUT a back).
    // Consuming history (push false, i.e. Back/Forward) pops instead.
    this._canvasStack = this._canvasStack || [];
    if (reload) {
      /* in-place re-render: leave the stack alone */
    } else if (push && this._contentOpen) {
      let targetIsArticle = false;
      let prevIsBlog = false;
      try {
        const targetPath = this.#localPath(new URL(url, window.location.href).pathname);
        targetIsArticle = /^\/blogs\/[^/]+\/[^/?#]+$/.test(targetPath) && !targetPath.includes('/tagged/');
        if (this._contentUrl) {
          const prevPath = this.#localPath(new URL(this._contentUrl, window.location.href).pathname);
          prevIsBlog = /^\/blogs\/[^/]+(\/tagged\/[^/?#]+)?$/.test(prevPath);
        }
      } catch (_) {
        /* unparsable URL → no back level */
      }
      if (targetIsArticle) {
        if (prevIsBlog) this._canvasStack.push({ kind: 'history' });
        else if (this._localReopen) this._canvasStack.push({ kind: 'fn', fn: this._localReopen });
      }
    } else if (!push) {
      const top = this._canvasStack[this._canvasStack.length - 1];
      if (top && top.kind === 'history') this._canvasStack.pop();
    }
    this._contentUrl = url;
    if (!reload) this._localReopen = null;
    this.#renderCanvasBack();
    // Content views are real URLs: entering one pushes its path so sharing,
    // Back/Forward and analytics see the page the visitor is reading. push is
    // false when we're CONSUMING history (popstate) or booting a deep link
    // (the URL is already correct). preview_theme_id is carried over so
    // draft-theme preview sessions survive the rewrite; wh-bundle is
    // deliberately dropped — content URLs stay clean, config state lives in
    // the cart (and is re-mirrored by #updateUrl once back in the explorer).
    if (push && !reload && this.routeMode === 'full' && !(window.Shopify && window.Shopify.designMode)) {
      try {
        const target = new URL(url, window.location.href);
        const current = new URL(window.location.href);
        const preview = current.searchParams.get('preview_theme_id');
        if (preview && !target.searchParams.has('preview_theme_id')) {
          target.searchParams.set('preview_theme_id', preview);
        }
        if (target.pathname + target.search !== current.pathname + current.search) {
          window.history.pushState({ whEx: true, whContent: true }, '', target.pathname + target.search);
        }
        this._routeArmed = true; // user-driven navigation
      } catch (_) {
        /* unparsable url — render without touching history */
      }
    }
    this._contentOpen = true;
    if (!campaign && !reload) {
      // Which drawer BACK returns to: 'more' when the view was opened from
      // the product views drawer (e.g. the FAQ item — bug 2026-08-08: it
      // stamped 'menu', so BACK opened the site menu instead of See more,
      // a BLANK shell on phones where the nav is docked away).
      this._contentSource = source || 'menu';
      this.#applyTint(null); // tint belongs to campaigns only
    }
    this._contentToken = Symbol('content');
    const token = this._contentToken;
    if (this.refs.contentTitle) this.refs.contentTitle.textContent = title || '';
    // Responsive nesting is CSS-driven, deliberately not JS-branched: on
    // mobile the fullscreen sheet simply covers the source drawer (higher
    // z-index) and the back button appears via media query — no viewport
    // sniffing (window.innerWidth lies in backgrounded tabs).
    clearTimeout(this._contentHideT); // a pending close would re-hide mid-open
    content.hidden = false;
    void content.offsetWidth;
    content.classList.add('is-open');
    content.setAttribute('aria-hidden', 'false');
    this.#syncOverlayKeydown();
    // Never show stale content or a blank white canvas while fetching: clear
    // the body immediately and run the rune for at least the universal beat
    // (CANVAS_LOADER_MIN_MS) — longer loads extend it, cache hits still get
    // the consistent transition (user spec 2026-08-05).
    this.#canvasLoaderStart();

    try {
      const page = await this.#fetchHtml(url);
      if (!page.ok && !/text\/html/i.test(page.contentType)) {
        // Non-HTML errors have nothing renderable. HTML errors (Shopify
        // serves the themed 404 template with a 404 status) fall through
        // and render like any page — uncached, in case the URL comes alive.
        throw new Error(`Failed to load (${page.status})`);
      }
      const html = page.html;
      if (this._contentToken !== token || !this._contentOpen) return; // superseded
      const { main, pageTitle } = this.#adoptFetchedMain(html, url);
      if (!(await this.#canvasLoaderFinish(token))) return; // superseded mid-beat
      body.innerHTML = '';
      Array.from(main.children).forEach((child) => body.appendChild(document.adoptNode(child)));
      this.#applyCanvasOverrides(body);
      this.#appendCanvasFooter(body);
      // Safety net (2026-09-11): if the fetched page IS the current page, its
      // server-rendered copy is still in #MainContent under the explorer —
      // remove it so nothing renders twice or bleeds through the chrome.
      try {
        if (this.#localPath(new URL(url, window.location.href).pathname) === this.#localPath(window.location.pathname)) {
          this.#mainContentSections().forEach((el) => el.remove());
        }
      } catch (_) {
        /* unparsable url — leave the DOM alone */
      }
      if (this._pendingFormHydration) {
        this._pendingFormHydration();
        this._pendingFormHydration = null;
      }
      body.scrollTop = 0;
      if (this.refs.contentTitle && pageTitle && !campaign) {
        // Prefer the page's own title, trimmed of the store-name suffix.
        // Campaign opens skip this: the payload's campaign title was set
        // up-front and is authoritative — overwriting would re-flip the text.
        this.refs.contentTitle.textContent = pageTitle.split(/\s[–—-]\s/)[0].trim() || title || '';
      }
    } catch (err) {
      console.warn('[wh-explorer] content fetch failed', err);
      if (await this.#canvasLoaderFinish(token)) {
        body.textContent = (err && err.message) || 'Failed to load.';
      }
    } finally {
      // Safety only — #canvasLoaderFinish hides the rune on every live path.
      if (this._contentToken === token && this.refs.contentLoader) this.refs.contentLoader.hidden = true;
    }
  }

  #closeContent({ keepOverlay = false, updateUrl = true } = {}) {
    const content = this.refs.content;
    if (!content) return;
    const wasOpen = this._contentOpen;
    this._contentOpen = false;
    // Leaving the canvas resets its back stack — the next open starts at
    // level zero.
    this._canvasStack = [];
    this._contentUrl = null;
    this._localReopen = null;
    this.#renderCanvasBack();
    // Leaving a content URL lands back in the live explorer, so the address
    // bar must follow (pushState — Back returns to the article). updateUrl is
    // false when the caller manages history itself (#interceptLink's owned
    // branch pushes the owned URL right after; #onPopState is consuming an
    // entry, not creating one).
    if (updateUrl && wasOpen && this.routeMode === 'full' && !(window.Shopify && window.Shopify.designMode)) {
      this._routeArmed = true; // user-driven navigation
      this.#updateUrl();
    }
    content.classList.remove('is-open');
    content.setAttribute('aria-hidden', 'true');
    clearTimeout(this._contentHideT);
    this._contentHideT = setTimeout(() => {
      content.hidden = true;
    }, 280); // opacity/translate transition + margin — keep in sync with the CSS
    if (!keepOverlay) {
      // Un-hoist fetched-page stylesheets: they are global while the canvas
      // is open and must not keep restyling the live explorer after it
      // closes. Module scripts stay (modules can't be un-executed; the
      // per-URL Set already dedupes them).
      if (this._contentStyles) {
        this._contentStyles.forEach((l) => l.remove());
        this._contentStyles.clear();
      }
      // CLOSE exits the overlay entirely: canvas, views drawer AND menu all
      // go — the visitor lands back in the live explorer (user decision
      // 2026-07-20; BACK is the "one level up" affordance instead).
      this.#applyTint(null);
      this.#closeMoreNav();
      this.#closeMenu();
      this.#syncOverlayKeydown();
    }
  }

  // Canvas back button visibility: only when there is somewhere to go back
  // to (user spec 2026-08-03) — never on the canvas's first view.
  #renderCanvasBack() {
    const btn = this.refs.contentHistoryBack;
    if (btn) btn.hidden = !(this._canvasStack && this._canvasStack.length);
  }

  // Pop one canvas level. History entries go through the browser (the pop
  // lands in #openContent push:false, which pops the stack); local-view
  // entries rebuild the view directly (they never had a URL).
  #canvasBack() {
    const stack = this._canvasStack || [];
    const top = stack[stack.length - 1];
    if (!top) return;
    if (top.kind === 'fn') {
      stack.pop();
      this.#renderCanvasBack();
      const reopen = top.fn;
      this.#closeContent({ keepOverlay: true });
      reopen();
    } else {
      window.history.back();
    }
  }

  // Product titles follow the view in full route mode; the summary and the
  // default view restore the server-rendered title.
  // ONE display-title resolution for every surface (panel, drawer mirror,
  // added chip, document title): spec displayTitle (when the surface has the
  // resolved specs at hand) → bundleDisplayTitle → the product's real title.
  // Price line for a variant: guarded (payload-external lines have no
  // priceCents) and multiplied by qty. Blank when unpriceable.
  #priceLineFor(variant, qty) {
    return variant && Number.isFinite(variant.priceCents) ? this.#money(variant.priceCents * (qty || 1)) : '';
  }

  #displayTitleFor(product, specs) {
    // Product metafield custom.explorer_product_title_overwrite (user
    // 2026-08-21): the per-product surgical override — beats EVERYTHING,
    // including the category's use_product_names_as_titles flag (that flag
    // is a category-wide default; this override exists precisely to fix
    // the odd product out).
    if (product && product.titleOverride) return product.titleOverride;
    // Category flag use_product_names_as_titles (user 2026-08-18): the
    // product's REAL title wins over spec titles/overwrites everywhere —
    // panel, drawer mirror, chip, review list (cart lines already carry it).
    if (product && product.useProductTitle) return product.title || '';
    return (specs && specs.displayTitle) || (product && (product.bundleDisplayTitle || product.title)) || '';
  }

  #syncTitle(product) {
    if (!this._baseTitle) return;
    if (product && !this.#isSummary(this.state.currentKey)) {
      const name = this.#displayTitleFor(product);
      const brand = this.labels.wordmark || '';
      document.title = brand ? `${name} — ${brand}` : name;
    } else {
      document.title = this._baseTitle;
    }
  }

  // Build the keyed model for one catalog and make it the current one.
  // BROWSING state (what the visitor is looking at per category — product,
  // variant, staged qty) is cached per catalog handle so switching back
  // restores it; picks re-derive from the last-known cart because they ARE
  // cart lines, not catalog state.
  #installCatalog(idx) {
    const cat = this.catalogs[idx];
    if (!cat) return;
    this._catalogIdx = idx;
    // Styling hook: the wordmark shows a pointer in the museum (it acts as
    // "back to shop" there — see the wordmark click handler).
    this.classList.toggle('is-museum', idx === 1);
    // Dynamic market visibility (user 2026-08-21): the payload serializes
    // per REQUEST, so each category's products list already reflects this
    // market's publications. A category with no products here disappears
    // entirely — nav chips, journeys, routing, arrows and the drawer all
    // derive from the pruned model and already tolerate missing keys, while
    // cart lines bought in another market still resolve against the RAW
    // catalogs (summary boxes, titles). Empty sub-categories prune off
    // their parent. A parent with no own products cannot render a scene, so
    // it prunes even when a sub survives (warned — that is a data smell to
    // fix in admin, not a rendering choice). Fail-open: a market where
    // EVERY category is empty keeps the raw list — broken data must not
    // blank the site.
    const marketSteps = cat.steps
      .map((step) => {
        const own = Array.isArray(step.products) && step.products.length > 0;
        const subs = (step.subSteps || []).filter(
          (s) => s && Array.isArray(s.products) && s.products.length > 0
        );
        if (!own) {
          if (subs.length) {
            console.warn(
              '[wh-explorer] category',
              step.key,
              'has no products in this market but a sub-category does — pruning both; give the parent a published product to restore them'
            );
          }
          return null;
        }
        if (subs.length === (step.subSteps || []).length) return step;
        return { ...step, subSteps: subs };
      })
      .filter(Boolean);
    const steps = marketSteps.length ? marketSteps : cat.steps;
    // Every key (main or sub) is a state identity — cart _wh_category lines,
    // picks, browse — so it must appear ONCE in the catalog. The same
    // category referenced as a sub under two parents (or as both a main and
    // a sub) keeps its FIRST slot; later duplicates are dropped with a warn.
    const seenKeys = new Set(
      steps.map((s) => (s.key === this.summaryKey ? `${s.key}-category` : s.key))
    );
    this.realSteps = steps.map((step, i) => {
      // De-dup guard: keys come from metaobject handles; a collision with the
      // summary sentinel would corrupt keyed state.
      const key = step.key === this.summaryKey ? `${step.key}-category` : step.key;
      return {
        ...step,
        key,
        filters: this.#installFilters(step),
        isHero: i === 0, // first category = summary center slot + single-quantity
        // Sub-categories (self-referencing list on the category metaobject,
        // serialized as full steps). They join the keyed model below — own
        // browse/pick state, own scene/summary params — but NOT the main chip
        // row: they render as a second chip row under their parent, and the
        // post-ADD flow visits them before the next main category (#nextKey).
        subSteps: (step.subSteps || [])
          .filter((s) => s && s.key && Array.isArray(s.products))
          .map((s) => ({
            ...s,
            key: s.key === this.summaryKey ? `${s.key}-category` : s.key,
            parentKey: key,
            filters: this.#installFilters(s),
            isHero: false,
          }))
          .filter((s) => {
            if (s.key === key) return false; // self-reference
            if (seenKeys.has(s.key)) {
              console.warn(
                '[wh-explorer] sub-category',
                s.key,
                'already placed elsewhere in the catalog — keeping its first slot, ignoring it under',
                key
              );
              return false;
            }
            seenKeys.add(s.key);
            return true;
          }),
      };
    });
    this.stepKeys = this.realSteps.map((s) => s.key);
    this.subSteps = this.realSteps.flatMap((s) => s.subSteps);
    this.steps = this.realSteps.flatMap((s) => [s, ...s.subSteps]).concat([
      {
        key: this.summaryKey,
        label: this.labels.summary || 'Summary',
        description: '',
        sceneType: 'cart_summary',
        scenePropModelUrl: '',
        products: [],
        isHero: false,
      },
    ]);
    this.categories = new Map(this.steps.map((s) => [s.key, s]));
    if (!this._browseCache[cat.handle]) {
      const browse = {};
      this.realSteps.concat(this.subSteps).forEach((s) => {
        browse[s.key] = {
          productId: s.products[0]?.id ?? null,
          variantId: s.products[0]?.defaultVariantId ?? null,
          qty: 1,
          filter: null, // active filtration category key (null = all)
        };
      });
      this._browseCache[cat.handle] = browse;
    }
    this.state.browse = this._browseCache[cat.handle];
    // Land on the first step that HAS products: merchant data churn (a
    // product unpublished from the market mid-edit) can empty step 0, and
    // booting into an empty scene reads as a dead site (seen 2026-08-05 —
    // K-001 unpublished emptied the keyboard step). Flow order and hero
    // mechanics are untouched; this only picks the initial view.
    const firstWithProducts = this.realSteps.find((s) => s.products && s.products.length > 0);
    this.state.currentKey = (firstWithProducts && firstWithProducts.key) ?? this.stepKeys[0] ?? this.summaryKey;
    // Re-derive picks against THIS catalog's categories. Cart lines from the
    // other catalog stay in the cart (and in the count) — they just have no
    // chip here.
    if (this._cart) this.#applyCart(this._cart);
    else this.state.picks = {};
  }

  // Step to the previous/next category in nav order (the mobile strip's
  // arrow buttons). Clamped at the ends — #renderStepsNav disables the arrows.
  // Mobile docking (one node, two homes — the established pattern): below
  // 750px the see-more button joins the action bar (Figma 458/459: bordered
  // box beside ADD) and the site nav moves into the left drawer's SITE tab;
  // on desktop both return to their original homes.
  #setupMobileDocks() {
    const mq = window.matchMedia('(max-width: 749px)');
    this._dockMq = mq; // held for disconnect cleanup
    const place = () => {
      const mobile = mq.matches;
      const more = this.refs.panelMore;
      const bar = this.querySelector('.wh-ex__actionbar');
      const links = this.querySelector('.wh-bc__panel-links');
      if (more && bar && links) {
        const target = mobile ? bar : links;
        if (more.parentElement !== target) {
          if (mobile) bar.insertBefore(more, bar.firstChild);
          else links.appendChild(more);
        }
      }
      const nav = this.refs.menuNav;
      const site = this.refs.mnavSite;
      const menu = this.refs.menu;
      if (nav && site && menu) {
        const target = mobile ? site : menu;
        if (nav.parentElement !== target) target.appendChild(nav);
      }
      // The see-more BUY BLOCK leaves the drawer on phones: the drawer is
      // TRANSFORMED (slide-in), and a transformed ancestor turns
      // position:fixed into absolute-within-it — the floating bar was
      // trapped inside the menu (user 2026-08-14). Same dock pattern as
      // everything else; CSS shows it only while the drawer is open.
      const buy = this.refs.moreBuy;
      const root = this.refs.root;
      if (buy && root) {
        if (mobile) {
          if (buy.parentElement !== root) root.appendChild(buy);
        } else if (this.refs.moreBuyAnchor && buy.parentElement === root) {
          this.refs.moreBuyAnchor.after(buy);
        }
      }
      // Title + description live at the TOP of the screen on phones
      // (mobile rework 2026-08-13); anchors mark their desktop spots.
      const top = this.refs.mobileTop;
      const title = this.refs.panelTitle;
      const desc = this.refs.panelDescription;
      // Pre-order note rides along under the description in the top dock
      // (user 2026-08-14: was above the variants/CTA at the bottom).
      const pre = this.refs.panelPreOrder;
      if (top && title && desc) {
        if (mobile) {
          if (title.parentElement !== top) top.append(title, desc);
          if (pre && pre.parentElement !== top) top.append(pre);
          top.hidden = false;
        } else {
          top.hidden = true;
          if (this.refs.panelTitleAnchor && title.parentElement === top) {
            this.refs.panelTitleAnchor.after(title);
          }
          if (this.refs.panelDescAnchor && desc.parentElement === top) {
            this.refs.panelDescAnchor.after(desc);
          }
          if (pre && this.refs.panelPreAnchor && pre.parentElement === top) {
            this.refs.panelPreAnchor.after(pre);
          }
        }
      }
    };
    place();
    this._onDockChange = () => {
      place();
      // Breakpoint flip: the drawers have different homes per side — close
      // whichever was open so a stranded mobile drawer never lingers on
      // desktop (seen when a window boots small and grows).
      this.#closeMenu(); // funnels #closeMobileNav too
    };
    mq.addEventListener('change', this._onDockChange);
  }

  #openMobileNav = () => {
    const el = this.refs.mobileNav;
    if (!el) return;
    // Only close the DESKTOP menu if it is actually open. #closeMenu also
    // closes any non-'more' canvas ("closing the menu closes the canvas"),
    // so calling it unconditionally here closed every phone deep link a
    // frame after boot: #start opens the canvas, then #openMenu → this →
    // #closeMenu → #closeContent (Kojima campaign, user 2026-09-04 —
    // affected K-001 identically). On phones the desktop menu is never
    // open (#openMenu routes here), so nothing is lost by the guard.
    if (this.refs.menu && this.refs.menu.classList.contains('is-open')) this.#closeMenu();
    this.#closeMoreNav();
    if (!el.classList.contains('is-open')) {
      this._mnavReturnFocus = /** @type {HTMLElement|null} */ (document.activeElement);
    }
    clearTimeout(this._mnavHideT);
    el.hidden = false;
    void el.offsetWidth;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    if (this.refs.menuButtonMobile) this.refs.menuButtonMobile.setAttribute('aria-expanded', 'true');
    this.#syncOverlayKeydown();
    this.#renderMobileNav();
  };

  #closeMobileNav = () => {
    const el = this.refs.mobileNav;
    if (!el || el.hidden) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (this.refs.menuButtonMobile) this.refs.menuButtonMobile.setAttribute('aria-expanded', 'false');
    clearTimeout(this._mnavHideT);
    this._mnavHideT = setTimeout(() => {
      el.hidden = true;
    }, 320); // keep in sync with the drawer transition
    this.#syncOverlayKeydown();
    if (this._mnavReturnFocus && typeof this._mnavReturnFocus.focus === 'function') {
      this._mnavReturnFocus.focus();
    }
    this._mnavReturnFocus = null;
  };

  #setMobileNavTab(tab) {
    const products = tab === 'products';
    if (this.refs.mnavTabProducts) this.refs.mnavTabProducts.classList.toggle('is-active', products);
    if (this.refs.mnavTabSite) this.refs.mnavTabSite.classList.toggle('is-active', !products);
    if (this.refs.mnavProducts) this.refs.mnavProducts.hidden = !products;
    if (this.refs.mnavSite) this.refs.mnavSite.hidden = products;
  }

  // The PRODUCT tab: top-level categories (styled like the site menu links)
  // + the museum/shop switcher when a second catalog exists. Selecting a
  // category steers the scene and closes the drawer.
  #renderMobileNav() {
    const host = this.refs.mnavProducts;
    if (!host) return;
    host.innerHTML = '';
    const mainKey = this.#mainKeyFor(this.state.currentKey);
    this.realSteps.forEach((step) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wh-ex__pill wh-ex__menu-link wh-ex-glass-hover wh-ex__mnav-item';
      btn.classList.toggle('is-active', step.key === mainKey);
      btn.textContent = step.label || '';
      btn.addEventListener('click', () => {
        this.#goToStep(step.key);
        this.#closeMobileNav();
      });
      host.appendChild(btn);
      // Filtration categories of the ACTIVE category, indented under it.
      const filters = step.key === mainKey && this.state.currentKey === mainKey ? this.#filtersFor(step) : [];
      if (filters.length) {
        const browse = this.state.browse[step.key];
        const active = (browse && browse.filter) || '';
        const frow = document.createElement('div');
        frow.className = 'wh-ex__mnav-filters';
        filters.forEach((f) => {
          const fb = document.createElement('button');
          fb.type = 'button';
          fb.className = 'wh-ex__pill wh-ex__menu-link wh-ex-glass-hover wh-ex__mnav-item wh-ex__mnav-filter';
          fb.classList.toggle('is-active', f.key === active);
          fb.textContent = f.label || '';
          fb.addEventListener('click', () => {
            this.#setFilter(step.key, f.key);
            this.#closeMobileNav();
          });
          frow.appendChild(fb);
        });
        host.appendChild(frow);
      }
    });
    if (this.catalogs.length > 1) {
      // Museum entry at the DRAWER BOTTOM as a glass menu row (user mock
      // 2026-08-14): destination label left, hint right (hint only when the
      // destination is the museum — "see past drops").
      const toMuseum = this._catalogIdx !== 1;
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'wh-ex__pill wh-ex__menu-link wh-ex-glass-hover wh-ex__mnav-switch';
      const lbl = document.createElement('span');
      lbl.textContent = toMuseum
        ? this.labels.museumButton || 'MUSEUM'
        : this.labels.shopButton || 'SHOP';
      sw.appendChild(lbl);
      if (toMuseum && this.labels.museumHint) {
        const hint = document.createElement('span');
        hint.className = 'wh-ex__mnav-switch-hint';
        hint.textContent = this.labels.museumHint;
        sw.appendChild(hint);
      }
      sw.addEventListener('click', () => {
        this.#switchCatalog(1 - this._catalogIdx);
        this.#closeMobileNav();
      });
      host.appendChild(sw);
    }
  }

  // Swap the whole experience between catalogs (shop ⇄ museum): rune veil,
  // rebuild the keyed model, land on the new catalog's first category. The
  // renderer and canvas persist — the scene reconciles models as usual.
  #switchCatalog(idx) {
    if (idx === this._catalogIdx || !this.catalogs[idx]) return;
    this._routeArmed = true; // user-driven navigation
    this.#transition({ rune: true, hold: VEIL_SUMMARY_HOLD_MS }, () => {
      this.#installCatalog(idx);
      this.#renderStep(this.state.currentKey);
      this.#updateUrl();
    });
  }

  // Boot the explorer: rune pre-loader from the very first frame (held at
  // least #preloaderMin and until the scene reports ready — #hideLoader),
  // first render, permalink write. Runs once per connect — there is no
  // open/close lifecycle. Safety timeout drops the veil if models stall.
  #start() {
    this._openedAt = performance.now();
    this._preloaderMinMs = this.#preloaderMin();
    this.#veilShow(true);
    clearTimeout(this.loaderTimeout);
    this.loaderTimeout = setTimeout(() => this.#hideLoader(), 8000);
    this.#renderStep(this.state.currentKey);
    this.#updateUrl();
    // Deep-link boot overlays (set by #applyInitialState): a content URL
    // opens the canvas over the scene with the page's own server-rendered
    // sections; a campaign URL opens the full campaign experience.
    if (this._bootContent) {
      this.#openContentFromDom(this._bootContent.title);
      // Deep links land with the same chrome as in-session browsing: the
      // menu drawer opens beside the canvas (campaign deep links get the
      // product views drawer via #openCampaign instead). On mobile the
      // fullscreen content sheet simply covers it.
      this.#openMenu();
      this._bootContent = null;
    } else if (this._bootCampaign) {
      // push only for the homepage landing (boots from `/`); a campaign deep
      // link's URL is already correct.
      this.#openCampaign(this._bootCampaign, { push: !!this._bootLandingPush, fromDom: !this._bootLandingPush });
      this._bootCampaign = null;
      this._bootLandingPush = false;
    } else if (this._bootLanding) {
      // Homepage landing on a non-campaign URL: fetch it as content over the
      // default scene, with the same chrome as a content deep link (menu
      // drawer beside the canvas).
      this.#openContent(this._bootLanding, '');
      this.#openMenu();
      this._bootLanding = null;
    }
  }

  // Lift the page's own server-rendered content sections (they sit in the
  // HTML under the fixed explorer — that's what crawlers index) into the
  // canvas. Moving the live nodes keeps any initialized components working.
  #openContentFromDom(title) {
    const content = this.refs.content;
    const body = this.refs.contentBody;
    if (!content || !body) return;
    const main = document.getElementById('MainContent') || document.body;
    const ownSection = this.closest('.shopify-section');
    const sections = Array.from(main.children).filter(
      (el) => el !== ownSection && !el.querySelector('wh-explorer')
    );
    if (!sections.length) return;
    this._contentOpen = true;
    this._contentSource = 'route'; // initial server-rendered load — not menu-coupled
    this._contentToken = Symbol('content');
    // Deep-link boot: the server-rendered view IS this location — record it
    // so a further in-canvas navigation can stack a back level onto it.
    this._contentUrl = window.location.pathname;
    this._localReopen = null;
    if (this.refs.contentTitle) this.refs.contentTitle.textContent = title || '';
    body.innerHTML = '';
    sections.forEach((el) => body.appendChild(el));
    this.#applyCanvasOverrides(body);
    this.#appendCanvasFooter(body);
    body.scrollTop = 0;
    content.hidden = false;
    void content.offsetWidth;
    content.classList.add('is-open');
    content.setAttribute('aria-hidden', 'false');
    this.#syncOverlayKeydown();
  }

  // Cold entries get the full branding beat; a warm session (any prior entry
  // in this tab session) gets a short veil so deep-link hopping isn't gated
  // on the rune every time, and the theme editor gets none at all.
  #preloaderMin() {
    if (window.Shopify && window.Shopify.designMode) return 0;
    try {
      if (sessionStorage.getItem(WARM_SESSION_KEY)) return PRELOADER_WARM_MS;
      sessionStorage.setItem(WARM_SESSION_KEY, '1');
    } catch (e) {
      // storage blocked (private mode) — fall through to the cold beat
    }
    return PRELOADER_MIN_MS;
  }

  #isSummary(stepKey) {
    return stepKey === this.summaryKey;
  }

  // True when `key` is a navigable category (main or sub) in the installed
  // catalog — used to decide whether a /collections/<handle> URL steers the
  // scene (category key === collection handle) or renders in the canvas.
  #isCategoryKey(key) {
    return this.categories ? this.categories.has(key) && !this.#isSummary(key) : false;
  }

  // Pre-order note resolution: the variant's own overwrite wins over the
  // product-level text while that variant is selected; blank → product-level
  // → none. Used by the panel AND the cart-line property.
  #preOrderTextFor(product, variant) {
    return (variant && variant.preOrderText) || (product && product.preOrderText) || '';
  }

  // Find { product, variant } for a variant id across ALL catalogs (steps +
  // subs) — cart adds resolve their pre-order note at add time from the
  // committed pick, which may not be the browsed product.
  #productForVariantId(variantId) {
    for (const cat of this.catalogs || []) {
      for (const s of cat.steps || []) {
        for (const st of [s, ...(s.subSteps || [])]) {
          for (const p of st.products || []) {
            const v = (p.variants || []).find((x) => x.id === variantId);
            // step/stepKey: the FIRST step containing the product (multi-
            // category products deterministically belong to it) — the
            // summary composition groups cart lines by it.
            if (v) return { product: p, variant: v, step: st, stepKey: st.key };
          }
        }
      }
    }
    return null;
  }

  // Resolve a /collections/<handle> URL to a category step key in the
  // INSTALLED catalog: exact key match first (the original contract), then
  // the categories' explicit `collection_mapping` bindings (merchant field,
  // 2026-08-04 — lets deliberately-named collections steer any category).
  // First binding wins if a collection is mapped twice. Null → the URL is
  // not a category view (renders in the canvas).
  #collectionStepKey(handle) {
    if (!this.categories) return null;
    if (this.#isCategoryKey(handle)) return handle;
    for (const [key, step] of this.categories) {
      if (this.#isSummary(key)) continue;
      if (step && Array.isArray(step.collectionHandles) && step.collectionHandles.includes(handle)) return key;
    }
    return null;
  }

  // === Filtration categories (user spec 2026-09-29) ===
  // A category's `filtration_categories` are FILTERS on its own rail: label +
  // product ids, nothing else — no scene, no state identity, no cart line.
  // Install-time: keep only ids that are in THIS step's (market-resolved)
  // products, drop filters left empty — a zero-product chip never renders.
  #installFilters(step) {
    const own = new Set((step.products || []).map((p) => p.id));
    return (step.filters || [])
      .filter((f) => f && f.key && Array.isArray(f.productIds))
      .map((f) => ({ key: f.key, label: f.label || f.key, productIds: f.productIds.filter((id) => own.has(id)) }))
      .filter((f) => f.productIds.length > 0);
  }

  #filtersFor(step) {
    return (step && Array.isArray(step.filters) && step.filters) || [];
  }

  // The products on the step's RAIL: the active filter's overlap, else all.
  // step.products stays the full list everywhere else (lookups by id/handle,
  // picks, summary, cart, share) — only the presentation narrows.
  #railProducts(step) {
    if (!step) return [];
    const browse = this.state.browse[step.key];
    const active = browse && browse.filter ? this.#filtersFor(step).find((f) => f.key === browse.filter) : null;
    if (!active) return step.products || [];
    const ids = new Set(active.productIds);
    return (step.products || []).filter((p) => ids.has(p.id));
  }

  // Select a filter on the current step. No ALL item (user 2026-09-29): the
  // category label itself shows the whole category — clicking it, or the
  // active filter again, clears the filter. The rail re-renders through the
  // plain scene crossfade; the browsed product moves to the filter's first
  // product when it isn't in it.
  #setFilter(stepKey, filterKey) {
    const step = this.categories.get(stepKey);
    const browse = this.state.browse[stepKey];
    if (!step || !browse) return;
    let next = filterKey && this.#filtersFor(step).some((f) => f.key === filterKey) ? filterKey : null;
    if (next && browse.filter === next) next = null; // active filter clicked again → all
    if (browse.filter === next) return;
    browse.filter = next;
    const rail = this.#railProducts(step);
    if (rail.length && !rail.some((p) => p.id === browse.productId)) {
      browse.productId = rail[0].id;
      browse.variantId = this.state.variantByProduct[rail[0].id] || rail[0].defaultVariantId;
    }
    this.state.justAdded = null;
    this._routeArmed = true;
    this.#transition({ rune: false, hold: VEIL_SCENE_HOLD_MS }, () => {
      this.#renderStep(stepKey);
      this.#updateUrl();
    });
  }

  // The next category after `key` in FLOW order (user spec 2026-08-03):
  // a parent with sub-categories flows into its subs first (add the keyboard →
  // see keyboard accessories next), then resumes the regular main sequence;
  // the summary after the last one; null after the summary itself. Subs with
  // no products are skipped.
  #nextKey(stepKey) {
    if (this.#isSummary(stepKey)) return null;
    const step = this.categories.get(stepKey);
    if (!step) return null;
    if (step.parentKey) {
      // On a sub: next sibling sub with products, else the parent's next
      // main category.
      const parent = this.categories.get(step.parentKey);
      const sibs = (parent && parent.subSteps) || [];
      const i = sibs.findIndex((s) => s.key === stepKey);
      const nextSib = i === -1 ? null : sibs.slice(i + 1).find((s) => s.products.length);
      return nextSib ? nextSib.key : this.#nextMainKey(step.parentKey);
    }
    const firstSub = (step.subSteps || []).find((s) => s.products.length);
    return firstSub ? firstSub.key : this.#nextMainKey(stepKey);
  }

  // The next MAIN category after `key` in nav order; the summary after the
  // last one.
  #nextMainKey(stepKey) {
    const idx = this.stepKeys.indexOf(stepKey);
    if (idx === -1) return null;
    return idx < this.stepKeys.length - 1 ? this.stepKeys[idx + 1] : this.summaryKey;
  }

  // The main-nav key for any category key: a sub maps to its parent (subs
  // occupy the parent's slot in the main chip row), everything else to itself.
  #mainKeyFor(stepKey) {
    const step = this.categories.get(stepKey);
    return (step && step.parentKey) || stepKey;
  }

  #renderStepsNav() {
    const nav = this.refs.stepsNav;
    if (!nav) return;
    // Two rows inside the one docked nav element (so re-docking moves both):
    // the main categories, and — while the active category is a parent with
    // sub-categories or one of its subs — that parent's sub-category chips.
    if (!this._stepsMainRow || this._stepsMainRow.parentElement !== nav) {
      nav.innerHTML = '';
      this._stepsMainRow = document.createElement('div');
      this._stepsMainRow.className = 'wh-bc__steps-row';
      this._stepsSubRow = document.createElement('div');
      this._stepsSubRow.className = 'wh-bc__nest';
      this._stepsSubRow.hidden = true;
      nav.append(this._stepsMainRow, this._stepsSubRow);
    }
    // Main labels. Arriving via the nest (click or walk auto-advance) keeps
    // the OWNER as the active main label — the visitor is at
    // "/owner/related-item", not at the top-level category of the same name
    // (user spec 2026-08-08). #nestContextOwner validates the stored context.
    const ctxOwner = this.#nestContextOwner();
    const mainKey = ctxOwner || this.#mainKeyFor(this.state.currentKey);
    this.#renderStepChips(this._stepsMainRow, this.realSteps, mainKey);
    // Nested "Related Products" (designer nest, user rework 2026-08-07,
    // amended same day): the directed journey renders as a nest under its
    // OWNER's label whenever that owner is the active main category —
    // ALWAYS, not only mid-walk (the walk only controls the automatic
    // send-down after an ADD). The old sub-category chip row and its
    // "all <parent>" entry are gone — journey steps (top categories AND
    // subs alike) live here as secondary items instead.
    // Filtration categories win over the directed journey when a category
    // carries both (user spec 2026-09-29): the SAME nest renders the filters
    // (header "Filtering", items ALL + filters) instead of the journey.
    this.#renderJourneyNest(mainKey);
    // The nav re-renders on every added/step change — piggy-back the cart count.
    this.#renderCartButton();
    // Catalog toggle: visible only when a second catalog (the museum)
    // exists. The switch is ON while the museum catalog is installed.
    if (this.refs.catalogSwitch) {
      const hasMuseum = this.catalogs.length > 1;
      this.refs.catalogSwitch.hidden = !hasMuseum;
      if (hasMuseum) {
        // The button names the DESTINATION: MUSEUM while in the shop, SHOP
        // while in the museum. Single line (Frame 456) — no sub-copy.
        const museumOn = this._catalogIdx === 1;
        if (this.refs.catalogSwitchLabel) {
          this.refs.catalogSwitchLabel.textContent = museumOn
            ? this.labels.shopButton || 'SHOP'
            : this.labels.museumButton || 'MUSEUM';
        }
        this.#sizeCatalogButton();
      }
    }
    // Keep the mobile drawer's product tab in sync while it's open.
    if (this.refs.mobileNav && !this.refs.mobileNav.hidden) this.#renderMobileNav();
  }

  // One chip row (main categories or a parent's sub-categories), updated in
  // place. The key signature guards the rebuild: same-length step sets with
  // DIFFERENT keys (catalog switch, changing sub parent) must not keep stale
  // chips — each button's click closure captures its step key at build time.
  #renderStepChips(row, steps, activeKey) {
    const sig = steps.map((s) => s.key).join('|');
    if (row.dataset.sig !== sig) {
      row.dataset.sig = sig;
      row.innerHTML = '';
      steps.forEach((step) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wh-bc__step-btn';
        btn.dataset.stepKey = step.key;
        const label = document.createElement('span');
        label.className = 'wh-bc__step-btn-label';
        btn.appendChild(label);
        btn.addEventListener('click', () => this.#goToStep(step.key));
        row.appendChild(btn);
      });
    }
    // Frame 456 redesign: labels only — the pick-state sub-line ("empty" /
    // picked product) is gone; the current step reads by weight (Bold vs
    // Light, CSS .is-active).
    Array.from(row.querySelectorAll('.wh-bc__step-btn')).forEach((btn, idx) => {
      const step = steps[idx];
      btn.classList.toggle('is-active', step.key === activeKey);
      btn.querySelector('.wh-bc__step-btn-label').textContent = step.label || '';
    });
  }

  // The designer's nested "Related Products" block (user rework 2026-08-07):
  // a grey header + the journey steps (indication arrows removed on
  // designer feedback 2026-08-08; the fold-out animation carries the
  // affordance instead). Shown whenever the active main
  // category OWNS a journey (amended 2026-08-07: always visible, so leaving
  // and returning to the category keeps the nest — the walk state only
  // drives the automatic post-ADD navigation). While a walk runs, the nest
  // anchors to the WALK's owner even when the visitor stands on a stop.
  // Items are the SAME steps as anywhere else — clicking navigates; the
  // active stop reads bold like a main label.
  // The validated nest context: the stored owner counts only while the
  // current step is inside that owner's chain (a hard route change away
  // leaves a stale owner behind — treat it as no context).
  // The owner whose journey chain surfaces this step in the nav nest — for
  // cold boots (reload / deep link): nestOwner is click-time state and isn't
  // persisted, so without re-seeding a journey member presents as its schema
  // parent (keyboard-accessories reloaded showed ACCESSORIES bold with no
  // nest — user 2026-08-19). First owner in nav order wins — deterministic.
  #nestOwnerFor(stepKey) {
    const owner = this.realSteps.find(
      (s) => Array.isArray(s.journey) && s.journey.includes(stepKey)
    );
    return owner ? owner.key : null;
  }

  #nestContextOwner() {
    const owner = this.state.nestOwner;
    if (!owner || this._catalogIdx !== 0) return null;
    const step = this.categories.get(owner);
    if (!step || !Array.isArray(step.journey) || !step.journey.length) return null;
    const chain = [owner, ...step.journey];
    return chain.includes(this.state.currentKey) ? owner : null;
  }

  #renderJourneyNest(mainKey) {
    const nest = this._stepsSubRow;
    if (!nest) return;
    const step = this.categories.get(mainKey);
    // Nest contents: { head, items: [{ key, label, active, onClick }] }.
    // Filtration categories (user spec 2026-09-29) take the nest over the
    // journey when a category carries both: header "Filtering", items ALL +
    // each non-empty filter, active = the selected filter (ALL when none).
    let head = null;
    let items = [];
    const filters = this.#filtersFor(step);
    if (filters.length) {
      const browse = this.state.browse[mainKey];
      const active = (browse && browse.filter) || '';
      head = this.labels.filtering || 'Filtering';
      items = filters.map((f) => ({
        key: `filter:${f.key}`,
        label: f.label || '',
        active: f.key === active,
        onClick: () => this.#setFilter(mainKey, f.key),
      }));
    } else if (step && Array.isArray(step.journey) && step.journey.length) {
      const chain = [mainKey, ...step.journey].filter((k) => this.categories.has(k));
      if (chain.length > 1) {
        head = this.labels.relatedProducts || 'Related Products';
        items = chain
          .slice(1)
          .map((k) => this.categories.get(k))
          .filter(Boolean)
          .map((s) => ({
            key: s.key,
            label: s.label || '',
            active: s.key === this.state.currentKey,
            onClick: () => this.#goToStep(s.key, { nestOwner: chain[0] }),
          }));
      }
    }
    const ownerBtn = this._stepsMainRow.querySelector(`[data-step-key="${CSS.escape(mainKey)}"]`);
    if (!head || !ownerBtn || !items.length) {
      nest.hidden = true;
      nest.dataset.sig = '';
      return;
    }
    nest.hidden = false;
    const sig = [mainKey, head, ...items.map((it) => it.key)].join('|');
    if (nest.dataset.sig !== sig) {
      nest.dataset.sig = sig;
      nest.innerHTML = '';
      const headEl = document.createElement('div');
      headEl.className = 'wh-bc__nest-head';
      headEl.textContent = head;
      headEl.style.setProperty('--wh-nest-i', '0');
      nest.appendChild(headEl);
      items.forEach((it, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wh-bc__step-btn wh-bc__nest-item';
        btn.dataset.stepKey = it.key;
        btn.style.setProperty('--wh-nest-i', String(i + 1));
        const label = document.createElement('span');
        label.className = 'wh-bc__step-btn-label';
        label.textContent = it.label;
        btn.appendChild(label);
        btn.addEventListener('click', it.onClick);
        nest.appendChild(btn);
      });
      // Fold-out: re-arm the container's ::before path-draw (the element
      // persists across rebuilds; the class toggle restarts its animation).
      nest.classList.remove('is-anim');
      void nest.offsetWidth;
      nest.classList.add('is-anim');
    }
    // Anchor the nest under the OWNER's label (the connector line drops from
    // just left of it). offsetLeft is relative to the positioned steps-nav.
    nest.style.left = `${ownerBtn.offsetLeft}px`;
    const activeKeys = new Set(items.filter((it) => it.active).map((it) => it.key));
    nest.querySelectorAll('.wh-bc__nest-item').forEach((btn) => {
      btn.classList.toggle('is-active', activeKeys.has(btn.dataset.stepKey));
    });
  }

  #productById(stepKey, productId) {
    const step = this.categories.get(stepKey);
    if (!step) return null;
    return step.products.find((p) => p.id === productId) || null;
  }

  #variantById(product, variantId) {
    if (!product) return null;
    return product.variants.find((v) => v.id === variantId) || null;
  }

  // A product is available when ANY of its variants is (payload emits every
  // variant with an `available` flag; sold-out products render in the scene
  // but present as SOLD OUT and can't be committed).
  #productAvailable(product) {
    return !!(product && product.variants && product.variants.some((v) => v.available !== false));
  }

  // The COMMITTED selection for an added category — what's actually in the
  // bundle. null when not added (presence in `picks` IS the added flag;
  // hydration always rebuilds picks, so no browse fallback is needed).
  #pickedFor(stepKey) {
    return this.state.picks[stepKey] || null;
  }

  // Specs for the CURRENT selection. Products with per-variant specs
  // (custom.fetch_specs_from_variant = true — e.g. the skates, whose shapes ×
  // materials differ) resolve from the selected variant's own entry; everything
  // else (and a variant without an entry) uses the product-level entry.
  #specsFor(product, variant) {
    if (product && product.specsFromVariant && variant && variant.specifications) {
      return variant.specifications;
    }
    return (product && product.specifications) || null;
  }

  #renderStep(stepKey) {
    this.state.currentKey = stepKey;
    const isSummary = this.#isSummary(stepKey);
    if (this.refs.panelStep) this.refs.panelStep.hidden = isSummary;
    if (this.refs.panelReview) this.refs.panelReview.hidden = !isSummary;
    // Summary topbar shows the wordmark only (design screen 5).
    if (this.refs.root) this.refs.root.classList.toggle('is-summary', isSummary);
    this.#renderStepsNav();
    this.#renderScene();
    if (isSummary) {
      this.#renderReview();
    } else {
      this.#renderPanel();
    }
  }

  #goToStep(stepKey, { nestOwner = null } = {}) {
    if (!stepKey || !this.categories.has(stepKey)) return;
    // Leaving the running journey's chain abandons the walk (navigating
    // WITHIN the chain keeps it — the visitor is just looking around).
    if (!this._journeyNav && this.state.journey) {
      const chain = this.state.journey.chain || [];
      if (!chain.includes(stepKey)) this.state.journey = null;
    }
    // Nest context (user spec 2026-08-08): arriving VIA the nest (click or
    // walk auto-advance) keeps the OWNER as the active main label with the
    // nested item highlighted — "/glass-pads/skates", not "/skates". Any
    // other navigation (incl. clicking the TOP-LEVEL chip of the same
    // category) clears the context and treats it as the plain top category.
    this.state.nestOwner = nestOwner;
    this.state.justAdded = null; // navigating restores the plain ADD face
    this._routeArmed = true; // user-driven navigation — real routes from here on
    if (stepKey === this.state.currentKey) {
      // Same STEP: an active filter clears (the label shows the whole
      // category); otherwise the nest context may have flipped (nested
      // skates ⇄ top-level skates are the same step key) — re-render the nav
      // so the active main label follows the context.
      const browse = this.state.browse[stepKey];
      if (browse && browse.filter) {
        this.#setFilter(stepKey, null);
        return;
      }
      this.#renderStepsNav();
      return;
    }
    // Crossfade the whole page between scenes (plain veil, ~0.5s); the way INTO
    // the summary AND back out of it get the rune load screen (the shorter
    // cousin of the pre-loader).
    const rune = this.#isSummary(stepKey) || this.#isSummary(this.state.currentKey);
    this.#transition({ rune, hold: rune ? VEIL_SUMMARY_HOLD_MS : VEIL_SCENE_HOLD_MS }, () => {
      this.#renderStep(stepKey);
      this.#updateUrl();
    });
  }

  #renderScene() {
    const step = this.categories.get(this.state.currentKey);
    if (!step) return;

    const sceneOpts = {
      sceneType: step.sceneType || 'products_3up',
      scenePropModelUrl: step.scenePropModelUrl || '',
      shadowOnModels: step.shadowOnModels !== false, // default true; false = cast a contact shadow under the placed model
      sheenIntensity: step.sheenIntensity, // optional 0–1 uniform sheen amount for this step's models; null = leave authored
      productDistance: step.productDistance, // optional rail spacing (metaobject); scene defaults to 0.50 when null
      productDistanceMobile: step.productDistanceMobile, // phone-only override; null → productDistance
      edgeSpacing: step.edgeSpacing === true, // category flag: distance = edge-to-edge gap
      productScale: step.productScale, // optional rig scale (metaobject); scene defaults to 1 (true to model) when null
      sceneYaw: step.sceneYaw, // optional rail-diagonal angle °; scene defaults to 30 when null
      scenePitch: step.scenePitch, // optional per-model pitch tilt °; defaults to 0
      sceneRoll: step.sceneRoll, // optional per-model roll tilt °; defaults to 0
      sceneYOffset: step.sceneYOffset, // optional vertical lift of the product composition — DESKTOP only (2026-09-14); defaults to 0
      sceneZoomMobile: step.sceneZoomMobile, // phones: fixed dolly-back for the vertical rail; defaults to 2
      sceneYOffsetMobile: step.sceneYOffsetMobile, // phones: fixed vertical lift; defaults to 0
      envIntensity: step.envIntensity, // optional per-category studio-environment intensity; null → the stage default (0.7)
      lightIntensity: step.lightIntensity, // optional per-category multiplier on the four directional lights; null → 1
      envMap: step.envMap, // optional per-category environment: 'studio' (default) | 'neutral'
    };

    if (this.#isSummary(this.state.currentKey)) {
      // The summary reflects the BASKET (user rework 2026-08-21): every cart
      // line contributes qty × its packaging box. Units:
      //  • default: ONE unit per CATEGORY at its placement ray — all of that
      //    category's boxes STACK on it (different products in one category
      //    stack together: sleeves on sleeves).
      //  • category flag summary_stack_products_individually: one unit per
      //    PRODUCT — the category breaks up, each product takes its own spot
      //    on the category's ray (the pull-together solver queues same-ray
      //    units outward), and multiples of that product stack on it.
      // Unit identity = the first-added line's product id (stable while that
      // line remains — same freeze dynamics as the old one-box-per-category).
      // Order: HERO-placement unit first (the layout anchor), the rest in
      // cart-line order, oldest first. Boxes without a packaging model are
      // skipped in 3D (existing rule; the review list still shows the line).
      const placementOf = (s) => {
        let pl = s.summaryBoxPlacement;
        if (Array.isArray(pl)) pl = pl[0];
        return String(pl || '').toUpperCase();
      };
      const lines = ((this._cart && this._cart.items) || []).slice().reverse(); // oldest first
      const units = new Map(); // insertion order = cart-line order
      lines.forEach((line) => {
        const found = this.#productForVariantId(line.variant_id);
        if (!found || !found.step) return;
        const pkg = (found.variant && found.variant.packagingModelUrl) || found.product.packagingModelUrl;
        if (!pkg) return;
        const indiv = found.step.summaryStackIndividually === true;
        const unitKey = indiv ? `${found.stepKey}::${found.product.id}` : found.stepKey;
        let unit = units.get(unitKey);
        if (!unit) {
          const placement = placementOf(found.step);
          unit = {
            ...found.product,
            packagingModelUrl: pkg, // base box = the unit's first-added line
            packagingStack: [],
            stepKey: found.stepKey,
            isHero: placement === 'HERO',
            summaryBoxScale: found.step.summaryBoxScale,
            summaryBoxPlacement: placement,
            summaryBoxDistance: found.step.summaryBoxDistance,
          };
          units.set(unitKey, unit);
        }
        const qty = line.quantity || 1;
        for (let i = 0; i < qty; i++) unit.packagingStack.push(pkg);
      });
      const added = Array.from(units.values());
      // Hero first — it's the anchor; a non-hero unit laying earlier would
      // slide into the empty center and the hero would land on top of it.
      added.sort((a, b) => (b.isHero ? 1 : 0) - (a.isHero ? 1 : 0));
      sceneOpts.products = added;
      sceneOpts.activeProductId = null;
    } else {
      const browse = this.state.browse[step.key];
      // A product outside the active filter can become the browsed one via
      // routing (product URL, campaign, share link, Back/Forward): the filter
      // yields — the rail shows the full category rather than hiding the
      // product the visitor navigated to.
      let rail = this.#railProducts(step);
      if (browse.filter && browse.productId && !rail.some((p) => p.id === browse.productId)) {
        browse.filter = null;
        rail = step.products;
      }
      const activeProductId = browse.productId ?? rail[0]?.id ?? null;
      browse.productId = activeProductId;
      sceneOpts.products = rail;
      sceneOpts.activeProductId = activeProductId;
    }

    if (this.scene) {
      this.scene.setScene(sceneOpts);
      this.#afterSceneRender();
      return;
    }
    // No scene yet → lazy-load the 3D module (three.js) on this first need.
    // Remember the latest opts so a step change during the one-time download
    // isn't lost, and guard so concurrent #renderScene calls don't double-boot.
    this._pendingSceneOpts = sceneOpts;
    if (this._sceneBooting) return;
    this._sceneBooting = true;
    this.#bootstrapScene();
  }

  // Dynamic-import the 3D module once, caching the promise so re-opens are
  // instant. Resolves to the WhBundleScene class.
  #loadSceneModule() {
    if (!this._sceneModulePromise) {
      this._sceneModulePromise = import(SCENE_MODULE).then((m) => m.WhBundleScene);
    }
    return this._sceneModulePromise;
  }

  // First-open path: build the stage host, await the 3D module, create the
  // scene. The pre-loader veil (shown by #onOpen) covers the module download
  // AND the first model load; the scene's onReady → #hideLoader drops it.
  async #bootstrapScene() {
    const sceneHost = document.createElement('div');
    sceneHost.className = 'wh-bc__scene-canvas';
    const tooltipLayer = document.createElement('div');
    tooltipLayer.className = 'wh-bc__scene-tooltips';
    this.refs.stage.appendChild(sceneHost);
    this.refs.stage.appendChild(tooltipLayer);
    // Scrolling down over the 3D view = "see more +" (user 2026-10-06). Bound
    // to the scene host only — the info panel is a sibling, so its own
    // scrolling never reaches this. Wheel = mouse/trackpad only; on phones a
    // vertical drag is already the rail swipe and stays that.
    sceneHost.addEventListener('wheel', this.#onSceneWheel, { passive: true });

    let SceneClass;
    try {
      SceneClass = await this.#loadSceneModule();
    } catch (err) {
      console.error('[wh-explorer] failed to load 3D module', err);
      this._sceneBooting = false;
      this.#hideLoader();
      return;
    }
    this._sceneBooting = false;
    // The element may have been disconnected (theme-editor section reload)
    // while the module downloaded — don't build into a detached host.
    if (!this.isConnected || !sceneHost.isConnected) {
      this.#hideLoader();
      return;
    }
    this.scene = new SceneClass({
      container: sceneHost,
      tooltipLayer,
      labelSafeArea: () => this.#labelSafeArea(),
      // (user 2026-08-12: "I do not want stuff jumping").
      onSelectProduct: (productId) => this.#setActiveProduct(productId),
      // Click on the product already in view = "see more +" (user 2026-10-06).
      onSelectActive: () => this.#openMoreFromScene(),
      onSwipeStep: (delta) => this.#stepProduct(delta),
      onReady: () => this.#hideLoader(),
      ...this._pendingSceneOpts, // always set by #renderScene before booting
    });
    this.#afterSceneRender();
  }

  // In-app browsers (X/Twitter, Instagram, FB…) overlay native chrome on the
  // BOTTOM of their webview without shrinking the viewport the page sees —
  // on X the ATC panel and options rendered under its URL bar (user
  // 2026-08-22; the page itself is fine at any honest viewport size, the
  // fixed inset/dvh root just can't see native overlays). Two nets, phones
  // only:
  //  • visualViewport truth: when the visible height is meaningfully
  //    smaller than the layout viewport, shrink the ROOT to it (inline px —
  //    the panel/review sheet are root-anchored and follow) and lift the
  //    viewport-FIXED bottom bar by the hidden delta. Skipped while a form
  //    field has focus: the keyboard shrinks visualViewport too, and
  //    re-laying the whole UI around it would jump.
  //  • UA net for webviews that report nothing: a fixed 56px lift, enough
  //    to clear X's URL bar.
  // IN-APP BROWSERS ONLY. Real Safari/Chrome position fixed elements inside
  // the visible area themselves, so they never need a lift — and iOS Safari
  // reports a visualViewport ~100px shorter than innerHeight around load
  // (toolbar transitions), which froze a permanent lift: the bottom bar
  // floated ~107px up with a dead grey strip under it and the summary sheet
  // shrunk with it (user 2026-09-06, iPhone screenshot). Outside a webview
  // the guard is inert; nothing is bound.
  #setupViewportGuard() {
    const inApp = /Twitter|TwitterApp|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(
      navigator.userAgent || ''
    );
    if (!inApp) return;
    const vv = window.visualViewport;
    const apply = () => {
      const ae = document.activeElement;
      if (ae && /INPUT|TEXTAREA|SELECT/.test(ae.tagName)) return;
      let lift = 0;
      if (window.innerWidth <= 749) {
        const delta = vv
          ? Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0))
          : 0;
        lift = delta > 24 ? Math.round(delta) : 56;
      }
      if (lift > 0) {
        if (this.refs.root) this.refs.root.style.height = `${window.innerHeight - lift}px`;
        this.style.setProperty('--wh-ex-vv-lift', `${lift}px`);
      } else {
        if (this.refs.root) this.refs.root.style.height = '';
        this.style.setProperty('--wh-ex-vv-lift', '0px');
      }
    };
    this._vvGuardApply = apply;
    if (vv) vv.addEventListener('resize', apply);
    window.addEventListener('resize', apply);
    apply();
  }

  // SmartBot bar-button companion (user 2026-08-27): with the chat button
  // enabled (section setting mobile_chat_label), the floating SmartBot
  // launcher is hidden on PHONES — the bar icon replaces it, reclaiming the
  // screen space.
  // The widget renders inside an OPEN shadow root on #smart-bot-container, so
  // page CSS can't reach it; a style is injected INTO the shadow root
  // instead. The launcher's class is CSS-module-hashed (_chat-icon-
  // container_<hash>), so the selector matches on the stable prefix — if
  // SmartBot ever renames the family, this degrades to showing the button
  // again (cosmetic only). The chat WINDOW portals into a sibling node and is
  // untouched. SmartBot loads async, so poll briefly until its container
  // mounts.
  #setupChatDock() {
    if (!this.querySelector('[data-wh-chat-open]')) return;
    const inject = () => {
      const host = document.getElementById('smart-bot-container');
      const sr = host && host.shadowRoot;
      if (!sr) return false;
      if (!sr.querySelector('#wh-chat-dock-style')) {
        const style = document.createElement('style');
        style.id = 'wh-chat-dock-style';
        style.textContent =
          '@media (max-width: 749px){ [class*="_chat-icon-container_"]{ display: none !important; } }';
        sr.appendChild(style);
      }
      return true;
    };
    if (inject()) return;
    let tries = 0;
    this._chatDockT = setInterval(() => {
      if (inject() || ++tries > 60) clearInterval(this._chatDockT);
    }, 500);
  }

  // The stage furniture the 3D hover labels must never sit under, measured live
  // and handed to the scene (which owns no explorer DOM). All three change size
  // at runtime, which is why none of it can be a constant in the 3D module: the
  // topbar's height follows its chips and fonts, the sub-category row only
  // exists while a parent category is open, and the panel's height tracks its
  // content (88px on an empty category, 364px with options + specs).
  #labelSafeArea() {
    const avoid = [];
    const push = (el) => {
      if (!el || el.hidden || el.hasAttribute('hidden')) return;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) avoid.push(r);
    };
    push(this.querySelector('.wh-bc__topbar'));
    push(this._stepsSubRow);
    push(this.refs.panel);
    push(this.refs.mobileBar); // phones: the fixed bottom bar (mobile fit reads it too)
    push(this.refs.mobileTop); // phones: the docked title/description block
    // Phones only: the summary's bottom review sheet — without it the camera
    // fit centred the composition in a "free strip" that ran BEHIND the
    // sheet (the mobile summary "sits low" report, user 2026-08-21).
    // Desktop deliberately keeps its accepted framing: the right-docked
    // review is NOT reported there.
    if (window.innerWidth <= 749) push(this.refs.panelReview);
    return { avoid };
  }

  // Post-scene reconcile + warming, shared by the create and re-render paths.
  #afterSceneRender() {
    // Reconcile EVERY product's displayed model with its remembered variant, so
    // a rebuilt rail (returning to this step, or a hydrated permalink) restores
    // each shape's picked colour — not just the active one. No-op per product
    // when it resolves to the model already shown.
    this.#pushAllVariantModels(this.state.currentKey);

    // Warm the next step's models (and their packaging boxes) while the
    // visitor browses this one. When the summary is next, warm the added
    // products' packaging so the set appears instantly.
    const nextKey = this.#nextKey(this.state.currentKey);
    const next = nextKey ? this.categories.get(nextKey) : null;
    if (next) {
      const toWarm = this.#isSummary(nextKey)
        ? Object.keys(this.state.picks)
            .map((k) => this.#productById(k, this.state.picks[k].productId))
            .filter(Boolean)
        : next.products;
      if (toWarm && toWarm.length) {
        clearTimeout(this._prefetchNextT); // re-render supersedes the pending warm
        this._prefetchNextT = setTimeout(() => {
          if (this.scene && this.isConnected) {
            this.scene.prefetch(toWarm);
          }
        }, 1500);
      }
    }
    // Warm the CURRENT step's VARIANT models too (base models are already
    // loading/loaded — the cache no-ops them): switching a keyboard colour
    // must swap instantly, not download on first click (user 2026-08-12).
    const here = this.categories.get(this.state.currentKey);
    if (here && here.products && here.products.length) {
      clearTimeout(this._prefetchHereT);
      this._prefetchHereT = setTimeout(() => {
        if (this.scene && this.isConnected) {
          this.scene.prefetch(here.products);
        }
      }, 2500);
    }
  }

  #setActiveProduct(productId) {
    const stepKey = this.state.currentKey;
    const step = this.categories.get(stepKey);
    const browse = this.state.browse[stepKey];
    if (!step || !browse) return;
    const product = step.products.find((p) => p.id === productId);
    if (!product) return;
    this._routeArmed = true; // user-driven navigation
    browse.productId = productId;
    // Restore this product's remembered variant (kept its colour/material/sound),
    // not the default — so switching back to a shape keeps what you picked.
    browse.variantId = this.state.variantByProduct[productId] || product.defaultVariantId;
    if (this.scene) {
      this.scene.setActiveProduct(productId);
      this.#pushVariantModel(stepKey); // land on the default variant's model
    }
    this.#renderPanel();
    this.#updateUrl();
  }

  // EXPERIMENT (vertical-mobile-rail): advance the active product by ±1
  // within the current step — one swipe (or dial tap) per step.
  #stepProduct(delta) {
    const step = this.categories.get(this.state.currentKey);
    const browse = this.state.browse[this.state.currentKey];
    const rail = this.#railProducts(step);
    if (!step || !browse || !rail.length) return;
    const idx = rail.findIndex((p) => p.id === browse.productId);
    const next = rail[Math.max(0, Math.min(rail.length - 1, idx + delta))];
    if (next && next.id !== browse.productId) this.#setActiveProduct(next.id);
  }

  // Vertical dots (phones, 2026-08-14): one dot per product in the current
  // step, active filled. Pure indicator — swipe is the navigation. Hidden
  // with <2 products and on the summary; CSS hides it on desktop.
  #renderVdots(step, activeProduct) {
    const host = this.refs.vdots;
    if (!host) return;
    const products = this.#railProducts(step);
    if (products.length < 2 || this.#isSummary(this.state.currentKey)) {
      host.hidden = true;
      return;
    }
    host.innerHTML = '';
    products.forEach((p) => {
      const dot = document.createElement('span');
      dot.className = 'wh-ex__vdot';
      if (activeProduct && p.id === activeProduct.id) dot.classList.add('is-active');
      host.appendChild(dot);
    });
    host.hidden = false;
  }

  #setActiveVariant(variantId) {
    const browse = this.state.browse[this.state.currentKey];
    if (!browse) return;
    this._routeArmed = true; // user-driven navigation
    browse.variantId = variantId;
    // Remember it against the active product so a later shape switch restores it.
    this.state.variantByProduct[browse.productId] = variantId;
    this.#pushVariantModel(this.state.currentKey); // swap the 3D model if this variant differs
    this.#renderPanel();
    this.#updateUrl();
  }

  // Push the active product's currently-selected variant model to the 3D scene.
  // The scene's swap-guard no-ops when the resolved .glb matches what's shown, so
  // material/sound changes that share a model never reload or flicker. Variant
  // model3dUrl falls back to the product-level URL (default variant / product mf).
  #pushVariantModel(stepKey) {
    if (!this.scene || this.#isSummary(stepKey)) return;
    const browse = this.state.browse[stepKey];
    const product = browse ? this.#productById(stepKey, browse.productId) : null;
    if (!product) return;
    this.scene.setVariantModel(product.id, this.#variantModelUrlFor(product));
    this.#pushVariantLabel(product);
  }

  // Push the variant-resolved hover-box label (header + teaser sub-line) to
  // the scene. Products with specsFromVariant (skates) read these off the
  // SELECTED variant's specifications entry — without this push the box kept
  // the product-level text whatever variant was picked (bug 2026-08-07).
  // For everything else specs resolve product-level, so the values match the
  // payload fields and the scene's fallback chain renders identically.
  #pushVariantLabel(product) {
    if (!this.scene) return;
    const variantId = this.state.variantByProduct[product.id] || product.defaultVariantId;
    const variant = variantId ? this.#variantById(product, variantId) : null;
    const specs = this.#specsFor(product, variant);
    this.scene.setProductLabel(product.id, {
      header: (specs && specs.displayTitle) || null,
      teaser: (specs && specs.teaser) || null,
    });
  }

  // Push EVERY product's remembered-or-default variant model so the whole step —
  // including inactive/orbiting products — reflects the saved configuration. Used
  // on scene (re)build so returning to a step restores each shape's picked colour.
  #pushAllVariantModels(stepKey) {
    if (!this.scene || this.#isSummary(stepKey)) return;
    const step = this.categories.get(stepKey);
    if (!step) return;
    step.products.forEach((p) => {
      this.scene.setVariantModel(p.id, this.#variantModelUrlFor(p));
      this.#pushVariantLabel(p);
    });
  }

  // A product's displayed model URL: its remembered variant's own model if set,
  // else the product base.
  #variantModelUrlFor(product) {
    // Remembered variant, else the DEFAULT variant (user bug 2026-08-12: a
    // keyboard whose default variant is Slate booted with the PRODUCT-level
    // model while the panel showed Slate selected — the first model must
    // always correspond to the selected variant; no admin re-upload needed).
    const variantId = this.state.variantByProduct[product.id] || product.defaultVariantId;
    const variant = variantId ? this.#variantById(product, variantId) : null;
    return (variant && variant.model3dUrl) || product.model3dUrl;
  }

  #renderPanel() {
    const stepKey = this.state.currentKey;
    const step = this.categories.get(stepKey);
    const browse = this.state.browse[stepKey];
    if (!step || !browse || this.#isSummary(stepKey)) {
      // The summary shows the review sheet — the previous step's product
      // dots must not linger (user 2026-08-18).
      if (this.refs.vdots) this.refs.vdots.hidden = true;
      return;
    }
    const activeProduct = this.#productById(stepKey, browse.productId);
    if (!activeProduct) {
      // Misconfigured/empty step (no resolvable product) — clear the panel so the
      // previous step's content doesn't linger as stale.
      if (this.refs.panelTitle) this.refs.panelTitle.textContent = '';
      if (this.refs.panelIcons) { this.refs.panelIcons.innerHTML = ''; this.refs.panelIcons.hidden = true; }
      if (this.refs.panelDescription) this.refs.panelDescription.textContent = '';
      if (this.refs.panelPreOrder) { this.refs.panelPreOrder.textContent = ''; this.refs.panelPreOrder.hidden = true; }
      if (this.refs.panelChips) { this.refs.panelChips.innerHTML = ''; this.refs.panelChips.hidden = true; }
      if (this.refs.panelOptions) { this.refs.panelOptions.innerHTML = ''; this.refs.panelOptions.hidden = true; }
      if (this.refs.panelPrice) this.refs.panelPrice.textContent = '';
      if (this.refs.panelMore) this.refs.panelMore.hidden = true;
      if (this.refs.productPager) this.refs.productPager.hidden = true;
      this.#applySceneBg(null);
      if (this.refs.panelAdd) this.refs.panelAdd.disabled = true;
      return;
    }
    const variantId = browse.variantId || activeProduct.defaultVariantId;
    const variant = this.#variantById(activeProduct, variantId) || activeProduct.variants[0];
    // Selection-aware specs (per-variant when the product opts in) — feeds the
    // feature-icon row AND the spec drawer, refreshed on every variant change
    // because #renderPanel re-runs on every change.
    const specs = this.#specsFor(activeProduct, variant);

    if (this.refs.panelTitle) {
      // Merchant-facing display name (product_specifications.bundle_display_title),
      // resolved from the SELECTED variant's specs entry when the product opts in
      // (specsFromVariant — same chain as the hover box, 2026-08-07); blank → the
      // product's ACTUAL title (deliberately not the spec/model name).
      this.refs.panelTitle.textContent = this.#displayTitleFor(activeProduct, specs);
    }
    if (this.refs.panelPreOrder) {
      // Pre-order disclosure (legal decision 2026-08-04): the same text that
      // rides the cart line as a visible property renders in the panel for
      // the SELECTED variant. Blank for in-stock items. (Never rendered
      // between the wave-2 rework and the launch QA — found 2026-08-17.)
      const variantId = browse.variantId || activeProduct.defaultVariantId;
      const variant = this.#variantById(activeProduct, variantId) || activeProduct.variants[0];
      const preText = this.#preOrderTextFor(activeProduct, variant);
      this.refs.panelPreOrder.textContent = preText || '';
      this.refs.panelPreOrder.hidden = !preText;
    }
    if (this.refs.panelIcons) {
      // Feature icons from the product_specifications `features` list (monochrome
      // SVGs, recolored to the panel text color via CSS mask). Only models that
      // carry features show them. The merchant `show_icons` toggle hides the row
      // (selection panel only, never the spec drawer) — settable per STEP on the
      // loadout/addon-group metaobject, or per MODEL on product_specifications;
      // an explicit false at either level wins.
      this.refs.panelIcons.innerHTML = '';
      // Icons follow the same opt-in model as the game labels (user spec
      // 2026-08-01): OFF by default, shown only when the CATEGORY enables
      // them — a product-level explicit false still wins as a veto.
      const iconsOn = step.showIcons === true && activeProduct.showIcons !== false;
      const feats = iconsOn
        ? ((specs && specs.features) || []).filter((f) => f && f.icon)
        : [];
      this.refs.panelIcons.hidden = feats.length === 0;
      feats.forEach((f) => {
        const span = document.createElement('span');
        span.className = 'wh-bc__panel-icon';
        span.style.setProperty('--wh-bc-icon-url', `url('${f.icon}')`);
        const img = document.createElement('img');
        img.src = f.icon;
        img.alt = f.label || '';
        img.setAttribute('aria-hidden', 'true');
        span.appendChild(img);
        this.refs.panelIcons.appendChild(span);
      });
    }
    if (this.refs.panelDescription) {
      this.refs.panelDescription.textContent =
        (specs && specs.panelDescription) || activeProduct.panelDescription || '';
    }
    // Museum meta rows (museum v2, 2026-08-13): studio / model / release
    // date from the exhibit's drops entry, each row only when filled.
    // Regular shop products carry no museumMeta, so the list stays hidden
    // there without any catalog check.
    if (this.refs.panelMuseum) {
      this.refs.panelMuseum.innerHTML = '';
      const meta = activeProduct.museumMeta || null;
      if (meta) {
        [['Studio', meta.studio], ['Model', meta.model], ['Release', meta.releaseDate]].forEach(([label, value]) => {
          if (!value) return;
          const div = document.createElement('div');
          div.className = 'wh-bc__panel-museum-row';
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = value;
          div.append(dt, dd);
          this.refs.panelMuseum.appendChild(div);
        });
      }
      this.refs.panelMuseum.hidden = this.refs.panelMuseum.children.length === 0;
    }
    this.#renderVdots(step, activeProduct);

    if (this.refs.panelChips) {
      this.refs.panelChips.innerHTML = '';
      // Game-type chips render only on categories that explicitly enable
      // them (step showGameLabels, default false — user spec 2026-07-31).
      const gameChips = step.showGameLabels === true ? activeProduct.useCases || [] : [];
      gameChips.forEach((chip) => {
        const li = document.createElement('li');
        li.className = 'wh-bc__chip';
        li.textContent = chip;
        this.refs.panelChips.appendChild(li);
      });
      // Hide when empty so it doesn't take a flex slot (phantom panel gap).
      this.refs.panelChips.hidden = this.refs.panelChips.children.length === 0;
    }

    this.#renderOptionAxes(activeProduct, variant);

    // Scene steppers are RETIRED (user 2026-08-14): every ADD adds one;
    // quantities are adjusted on the summary.
    const qty = 1;
    if (this.refs.panelPrice && variant) {
      this.refs.panelPrice.textContent = this.#money(variant.priceCents * qty);
    }
    // MUSEUM MODE: exhibits are never buyable — no CTA, no qty (the price
    // label lives inside the button, so it goes too). If a piece ever
    // becomes purchasable it gets added to the SHOP catalog instead (user
    // decision 2026-07-21). style.display, NOT the hidden attribute — the
    // button's CSS sets display:flex, which overrides [hidden]'s UA default.
    const museumMode = this._catalogIdx === 1;
    if (this.refs.panelAdd && museumMode) {
      this.refs.panelAdd.style.display = 'none';
      this.refs.panelAdd.onclick = null;
      if (this.refs.panelGoCart) this.refs.panelGoCart.hidden = true;
      if (this.refs.panelNext) this.refs.panelNext.hidden = true;
    }
    if (this.refs.panelAdd && !museumMode) {
      // Three faces (user 2026-08-14 — REPLACE/REMOVE retired, ADD is
      // repeatable):
      //   default                → ADD (adds one, every click)
      //   selection unavailable  → SOLD OUT (disabled)
      //   selection is preorder  → preorder label (still adds)
      // POST-ADD (Figma 589-591): right after a commit of THIS selection the
      // ADD swaps for [go to cart][see <next>] — the journey is a conscious
      // choice. Browsing any other product/variant restores ADD.
      const targetUnavailable =
        !this.#productAvailable(activeProduct) || (variant && variant.available === false);
      const targetPreorder = !!(variant && variant.preorder);
      const ja = this.state.justAdded;
      const showPostAdd =
        !!ja && ja.stepKey === stepKey && ja.productId === activeProduct.id &&
        (!variant || ja.variantId === variant.id);
      const nextKey = this.#journeyNextFor(stepKey);
      this.refs.panelAdd.style.display = showPostAdd ? 'none' : '';
      // Desktop's actionbar is display:contents (ADD is its own panel row);
      // the post-add PAIR needs a real flex row — toggled, so the plain ADD
      // layout stays byte-identical.
      const bar = this.refs.panelAdd.closest('.wh-ex__actionbar');
      if (bar) bar.classList.toggle('is-postadd', showPostAdd);
      if (this.refs.panelGoCart) {
        this.refs.panelGoCart.hidden = !showPostAdd;
        this.refs.panelGoCart.onclick = showPostAdd ? () => this.#goToSummary() : null;
      }
      if (this.refs.panelNext) {
        const nextStep = nextKey ? this.categories.get(nextKey) : null;
        this.refs.panelNext.hidden = !showPostAdd || !nextStep;
        if (this.refs.panelNextLabel) this.refs.panelNextLabel.textContent = nextStep ? nextStep.label || '' : '';
        this.refs.panelNext.onclick =
          showPostAdd && nextStep ? () => this.#goToJourneyStep(nextKey) : null;
      }
      if (!showPostAdd) {
        this.refs.panelAdd.disabled = false;
        let label = this.labels.add;
        let handler = () => this.#onCommitStep();
        if (activeProduct.externalUrl) {
          // External sales channel (user 2026-08-27): the product's
          // custom.external_url metafield holds a real URL for THIS market
          // (Amazon-exclusive markets — see wh-bundle-product-json for the
          // sentinel rules). The CTA links out in a new tab and wins over
          // every other face: in this market the product isn't sold here,
          // so availability and notify-me don't apply.
          label = activeProduct.externalCtaLabel || this.labels.externalCta || 'BUY NOW';
          handler = () => window.open(activeProduct.externalUrl, '_blank', 'noopener');
        } else if (targetUnavailable && variant && variant.notifyMe && step.turnNotifyIntoSoldOut !== true) {
          // TRUE sell-out (zero stock, no continue-selling): capture demand
          // instead of dead-ending (user 2026-08-18). Category flag
          // turn_notify_me_into_sold_out opts OUT per category (user
          // 2026-08-21): when the merchant wants to communicate a hard
          // sell-out, the branch below shows the disabled SOLD OUT button
          // instead of the notify sign-up.
          label = this.labels.notifyMe || 'NOTIFY ME';
          handler = () => this.#openNotifyDialog(activeProduct, variant);
          this.refs.panelAdd.disabled = false;
        } else if (targetUnavailable) {
          label = this.labels.soldOut || 'SOLD OUT';
          handler = null;
          this.refs.panelAdd.disabled = true;
        } else if (targetPreorder) {
          label = this.labels.preorder || 'SOLD OUT - PREORDER';
        }
        if (this.refs.panelAddLabel) this.refs.panelAddLabel.textContent = label;
        this.refs.panelAdd.onclick = handler;
      }
    }
    if (this.refs.panelMore) {
      const hasSpecs = !!(specs && (
        (specs.features && specs.features.length) ||
        (specs.contents && specs.contents.length) ||
        (specs.rows && specs.rows.length)
      ));
      const hasViews =
        hasSpecs ||
        !!activeProduct.campaignUrl ||
        !!activeProduct.faqUrl ||
        !!activeProduct.productDescription ||
        (activeProduct.images || []).length > 0 ||
        (activeProduct.articles || []).length > 0 ||
        this._catalogIdx === 0; // reviews exist for every shop product
      this.refs.panelMore.hidden = !hasViews;
      this.refs.panelMore.onclick = hasViews
        ? () => this.#openMoreNav(activeProduct, hasSpecs ? specs : null, {
            // Museum exhibits auto-open their CAMPAIGN page (user 2026-08-14)
            // the way shop products auto-open images & specs. On PHONES no
            // view auto-opens at all — the fullscreen canvas covered the
            // drawer, so "see more +" felt like a jump straight to specs
            // (user 2026-08-18): the drawer menu is the landing.
            autoOpen: window.matchMedia('(max-width: 749px)').matches
              ? null
              : this._catalogIdx === 1 && activeProduct.campaignUrl
                ? this.labels.moreAbout || 'about the product'
                : this.labels.moreImagesSpecs || 'images & specs',
          })
        : null;
      // "See more +" auto-opens the specs view, so hovering the trigger
      // warms that fetch (user item 7, 2026-08-07).
      this.refs.panelMore.onpointerenter = hasViews
        ? () => this.#prefetchContent(this.#templateViewUrl(activeProduct, this.viewTemplates.specs || 'specs'))
        : null;
    }
    // Scene background IMAGE hierarchy: product override > category override
    // > none (the scene color stays visible). Crossfaded between two layers.
    this.#applySceneBg(activeProduct.sceneBackground || step.sceneBackground || null);
    // See-more purchase block mirrors the panel AFTER its own state is fully
    // computed (qty, price, and the ADD button's label/disabled above):
    // title, description, ADD label/price/disabled copied verbatim. Hidden
    // in museum mode (no CTA there).
    if (this.refs.moreBuy) {
      const museumMode = this._catalogIdx === 1;
      this.refs.moreBuy.hidden = museumMode;
      if (!museumMode) {
        if (this.refs.moreBuyTitle) {
          this.refs.moreBuyTitle.textContent = this.#displayTitleFor(activeProduct, specs);
        }
        if (this.refs.moreBuyDesc) {
          this.refs.moreBuyDesc.textContent =
            (specs && specs.panelDescription) || activeProduct.panelDescription || '';
        }
        if (this.refs.moreBuyAdd && this.refs.panelAdd) {
          // Mirror the panel's face 1:1 — incl. the post-add pair. From the
          // canvas, go-to-cart/see-next first CLOSE the canvas + drawer so
          // the navigation lands on the live scene (user 2026-08-14).
          const postAdd = this.refs.panelAdd.style.display === 'none';
          this.refs.moreBuyAdd.hidden = postAdd;
          this.refs.moreBuyAdd.disabled = this.refs.panelAdd.disabled;
          if (this.refs.moreBuyAddLabel && this.refs.panelAddLabel) {
            this.refs.moreBuyAddLabel.textContent = this.refs.panelAddLabel.textContent;
          }
          if (this.refs.moreBuyAddPrice) {
            this.refs.moreBuyAddPrice.textContent =
              this.#priceLineFor(variant, qty);
          }
          this.#renderShipNote();
          const pairBox = this.refs.moreBuyGoCart && this.refs.moreBuyGoCart.parentElement;
          if (pairBox) pairBox.hidden = !postAdd;
          const closeAllThen = (fn) => () => {
            if (this.refs.moreClose) this.refs.moreClose.click();
            this.#closeContent();
            fn();
          };
          if (this.refs.moreBuyGoCart) {
            this.refs.moreBuyGoCart.hidden = !postAdd;
            this.refs.moreBuyGoCart.onclick = postAdd ? closeAllThen(() => this.#goToSummary()) : null;
          }
          if (this.refs.moreBuyNext) {
            const nk = this.#journeyNextFor(stepKey);
            const ns = nk ? this.categories.get(nk) : null;
            this.refs.moreBuyNext.hidden = !postAdd || !ns;
            if (this.refs.moreBuyNextLabel) this.refs.moreBuyNextLabel.textContent = ns ? ns.label || '' : '';
            this.refs.moreBuyNext.onclick =
              postAdd && ns ? closeAllThen(() => this.#goToJourneyStep(nk)) : null;
          }
        }
      }
    }
    // Product pager: flick affordance + "N of M" counter for the active
    // category. Arrows reuse #setActiveProduct — identical to clicking a
    // product in the 3D scene. Hidden for single-product categories.
    if (this.refs.productPager) {
      const pagerList = this.#railProducts(step);
      const pagerIdx = pagerList.findIndex((p) => p.id === activeProduct.id);
      const pagerShow = pagerList.length > 1 && pagerIdx !== -1 && !this.#isSummary(stepKey);
      this.refs.productPager.hidden = !pagerShow;
      if (pagerShow) {
        if (this.refs.pagerCount) {
          this.refs.pagerCount.textContent = `${pagerIdx + 1} ${this.labels.counterOf || 'of'} ${pagerList.length}`;
        }
        const pagerGo = (dir) => {
          const next = pagerList[(pagerIdx + dir + pagerList.length) % pagerList.length];
          if (next) this.#setActiveProduct(next.id);
        };
        if (this.refs.pagerPrev) this.refs.pagerPrev.onclick = () => pagerGo(-1);
        if (this.refs.pagerNext) this.refs.pagerNext.onclick = () => pagerGo(1);
      }
    }
  }

  // Option rows are derived from the active product's Shopify variant options
  // (skates: color / material / sound — color reskins the 3D model, the others
  // resolve a variant that shares the model). Switching the rail product itself
  // (shape: Dots ↔ Pills, or pad model) stays a 3D click — no chip — so the
  // visitor can never select a combination that doesn't exist as a variant.
  #renderOptionAxes(activeProduct, variant) {
    const container = this.refs.panelOptions;
    if (!container) return;
    container.innerHTML = '';
    this.#renderVariantRows(container, activeProduct, variant);
    // Hide when there are no variant rows so it doesn't add a phantom panel gap.
    container.hidden = container.children.length === 0;
  }

  // One row per Shopify variant option of the active product (color, material,
  // sound, ...). A multi-value option renders selectable chips; a single-value
  // option renders a static, non-interactive label (the option is fixed for this
  // shape — "static option" per design). Single-variant products render nothing.
  // "Title" is Shopify's placeholder option on default-variant products — never
  // a real row.
  #renderVariantRows(container, activeProduct, variant) {
    if (!activeProduct.variants || activeProduct.variants.length <= 1) return;
    const optionNames = activeProduct.optionNames || [];
    const currentOptions = variant ? variant.options.slice() : [];

    optionNames.forEach((name, idx) => {
      if (!name || name.toLowerCase() === 'title') return;
      const values = [];
      activeProduct.variants.forEach((v) => {
        const val = v.options[idx];
        if (val && !values.includes(val)) values.push(val);
      });
      if (values.length === 0) return;

      const currentValue = currentOptions[idx];

      const group = document.createElement('div');
      group.className = 'wh-bc__option-group';
      const label = document.createElement('span');
      label.className = 'wh-bc__option-group-label';
      label.textContent = name.toLowerCase();
      group.appendChild(label);

      // Single value → static label; the visitor has nothing to choose here.
      if (values.length < 2) {
        const staticVal = document.createElement('span');
        staticVal.className = 'wh-bc__option-static';
        staticVal.textContent = (currentValue || values[0] || '').toLowerCase();
        group.appendChild(staticVal);
        container.appendChild(group);
        return;
      }

      const chipsEl = document.createElement('div');
      chipsEl.className = 'wh-bc__option-chips';
      values.forEach((val) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'wh-bc__option-chip';
        chip.classList.toggle('is-selected', val === currentValue);
        chip.textContent = val.toLowerCase();
        // Prefer an AVAILABLE variant with this value; when none exists but
        // the combination itself EXISTS sold out, keep the chip clickable —
        // selecting it swaps the CTA to NOTIFY ME (live drop SOS, user
        // 2026-08-21: a sold-out variant became unselectable, locking
        // visitors out of the notify flow). Only combinations that exist on
        // NO variant at all stay disabled.
        let target = this.#resolveVariantWithValue(activeProduct, currentOptions, idx, val);
        if (!target) {
          target = this.#resolveVariantWithValue(activeProduct, currentOptions, idx, val, true);
          if (target) chip.classList.add('is-soldout');
        }
        if (!target) chip.disabled = true;
        chip.addEventListener('click', () => {
          if (target) this.#setActiveVariant(target.id);
        });
        chipsEl.appendChild(chip);
      });
      group.appendChild(chipsEl);
      container.appendChild(group);
    });
  }

  // Find a variant for picking `newValue` on axis `axisIdx`, cascading like
  // Shopify's own variant picker: EARLIER axes are held to the current
  // selection, LATER axes may re-resolve to whatever exists. So a value is
  // clickable only when it actually combines with what's already picked before
  // it (yellow selected → a silent that only exists in black returns null →
  // its chip disables), while picking an earlier axis can still legitimately
  // flip the later ones (yellow from black+silent lands on yellow+neutral).
  //
  // Only AVAILABLE variants count (all variants ship in the payload so every
  // option row renders; sold-out combinations grey out here instead). Fail-open:
  // a product with NO available variant at all (dev data / total sell-out,
  // reachable via the step-level fail-open) ignores availability so it stays
  // navigable.
  #resolveVariantWithValue(product, currentOptions, axisIdx, newValue, ignoreAvailability = false) {
    const anyAvailable = product.variants.some((v) => v.available !== false);
    const usable = (v) => ignoreAvailability || !anyAvailable || v.available !== false;
    const target = currentOptions.slice();
    target[axisIdx] = newValue;
    // Exact match — keep every other current selection.
    let v = product.variants.find((v) =>
      usable(v) && v.options.every((o, i) => o === target[i] || target[i] == null)
    );
    if (v) return v;
    // Cascade: hold axes 0..axisIdx (including the new value), relax later ones.
    v = product.variants.find((v) =>
      usable(v) && v.options.every((o, i) => i > axisIdx || o === target[i] || target[i] == null)
    );
    return v || null;
  }

  // Added-to-cart chip (user 2026-08-14): slide in (menu easing/width),
  // hold 3s, slide back out + fade. Phones: pinned just above the panel.
  #flashAdded(product, variant, qty) {
    const chip = this.refs.addedChip;
    if (!chip) return;
    // Card content (Figma 2328-9935): thumbnail, product title, the setting
    // copy as the sub-line, price right.
    if (this.refs.addedChipTitle) {
      const specs = this.#specsFor(product, variant);
      this.refs.addedChipTitle.textContent = this.#displayTitleFor(product, specs);
    }
    if (this.refs.addedChipLabel) {
      this.refs.addedChipLabel.textContent = this.labels.addedFeedback || 'added to cart';
    }
    if (this.refs.addedChipPrice) {
      this.refs.addedChipPrice.textContent =
        this.#priceLineFor(variant, qty);
    }
    if (this.refs.addedChipThumb) {
      // Variant image first (user 2026-08-31), same chain as the review
      // list — the added VARIANT's own picture, product-level fallback.
      const src =
        (variant && (variant.thumbnail || variant.image)) ||
        (product && (product.thumbnail || product.image)) || '';
      this.refs.addedChipThumb.src = src;
      this.refs.addedChipThumb.hidden = !src;
    }
    clearTimeout(this._addedT1);
    clearTimeout(this._addedT2);
    if (window.innerWidth <= 749 && this.refs.panel) {
      const r = this.refs.panel.getBoundingClientRect();
      chip.style.bottom = `${Math.round(window.innerHeight - r.top + 8)}px`;
      chip.style.top = 'auto';
    } else {
      chip.style.bottom = '';
      chip.style.top = '';
    }
    chip.hidden = false;
    void chip.offsetWidth; // restart the slide transition
    chip.classList.add('is-on');
    this._addedT1 = setTimeout(() => {
      chip.classList.remove('is-on');
      this._addedT2 = setTimeout(() => { chip.hidden = true; }, 450);
    }, 3000);
  }

  // Pending optimistic adds: committed clicks the server hasn't confirmed
  // yet. The badge counts them so it moves the instant ADD is pressed;
  // #applyCart (the reconcile) resets the counter along with real truth.
  _pendingAdds = 0;

  #onCommitStep() {
    const stepKey = this.state.currentKey;
    const browse = this.state.browse[stepKey];
    const product = browse ? this.#productById(stepKey, browse.productId) : null;
    if (!product) return;
    const variantId = browse.variantId || product.defaultVariantId;
    const variant = this.#variantById(product, variantId) || product.variants[0];
    if (!variant) return;
    // Belt for the SOLD OUT state: an unavailable selection never reaches the
    // cart (the button is disabled, but state can arrive by permalink too).
    if (variant.available === false || !this.#productAvailable(product)) return;
    // ADD is REPEATABLE (user 2026-08-14: no bundle semantics, no scene
    // stepper — every click adds one more; quantities are adjusted on the
    // summary). picks still records the LAST commit for the summary/permalink.
    const qty = 1;
    const prevPick = this.state.picks[stepKey];
    this.state.picks[stepKey] = { productId: product.id, variantId: variant.id, qty };
    this.#queueCommit(stepKey, variant.id, qty, prevPick);
    this.#flashAdded(product, variant, qty);
    // Directed journeys are a CONSCIOUS choice now (designer 2026-08-14):
    // adding never navigates — it arms the walk and swaps the CTA for
    // [go to cart][see <next>]. The auto-advance changed the scene behind
    // an open canvas, so the button pointed at the wrong step.
    const step = this.categories.get(stepKey);
    if (step && Array.isArray(step.journey) && step.journey.length && !this.state.journey && !this.#filtersFor(step).length) {
      const chain = [stepKey, ...step.journey].filter((k) => this.categories.has(k));
      if (chain.length > 1) this.state.journey = { chain };
    }
    this.state.justAdded = { stepKey, productId: product.id, variantId: variant.id };
    this._pendingAdds += 1;
    this.#renderCartButton();
    this.#renderPanel();
    this.#renderStepsNav();
  }

  // The next stop after stepKey on its journey (own journey, or the running
  // walk's chain) — null when the step has none or is the last stop.
  #journeyNextFor(stepKey) {
    const step = this.categories.get(stepKey);
    if (this.#filtersFor(step).length) return null; // filters win over the journey
    if (step && Array.isArray(step.journey) && step.journey.length) {
      const next = step.journey.find((k) => this.categories.has(k));
      if (next) return next;
    }
    const running = this.state.journey;
    if (running && Array.isArray(running.chain)) {
      const pos = running.chain.indexOf(stepKey);
      if (pos >= 0 && pos < running.chain.length - 1) return running.chain[pos + 1];
    }
    return null;
  }

  #goToSummary() {
    if (this.summaryKey) this.#goToStep(this.summaryKey);
  }

  // Journey advance: same step navigation, but flagged so #goToStep keeps
  // the running walk instead of reading it as the visitor leaving it.
  #goToJourneyStep(stepKey) {
    this._journeyNav = true;
    const owner = this.state.journey && this.state.journey.chain ? this.state.journey.chain[0] : null;
    this.#goToStep(stepKey, { nestOwner: owner });
    this._journeyNav = false;
  }

  // Top-right cart shortcut: live item count from the REAL cart when hydrated
  // (covers everything in it), summed picks before that. The .is-summary CSS
  // hides it on the summary itself; `hidden` covers the pre-render state.
  #renderCartButton() {
    const count =
      (this._cart
        ? this._cart.item_count
        : this.#collectLineItems().reduce((sum, item) => sum + item.qty, 0)) + this._pendingAdds;
    if (this.refs.cartCount) this.refs.cartCount.textContent = String(count);
    if (this.refs.cartCountMobile) this.refs.cartCountMobile.textContent = String(count);
    if (this.refs.cartButton) this.refs.cartButton.hidden = false;
    this.#renderShipNote(count);
  }

  // Free-shipping note under the ADD button (user 2026-10-13, TODO 40).
  // Eligibility is decided server-side from the storefront country
  // (labels.freeShippingEligible); the wording follows the live cart count:
  // empty → the offer, one item → "add 1 more", two or more → "you qualify".
  // Mirrored into the phone drawer's buy block. Hidden in the museum (no
  // ADD there) and wherever the merchant left the strings blank.
  #renderShipNote(count) {
    const note = this.refs.panelShipNote;
    const mirror = this.refs.moreBuyShipNote;
    if (!note && !mirror) return;
    const n = typeof count === 'number'
      ? count
      : (this._cart
          ? this._cart.item_count
          : this.#collectLineItems().reduce((sum, item) => sum + item.qty, 0)) + this._pendingAdds;
    let text = '';
    if (this.labels.freeShippingEligible && this._catalogIdx !== 1) {
      text = n >= 2
        ? this.labels.freeShippingQualified || ''
        : n === 1
          ? this.labels.freeShippingAddOne || ''
          : this.labels.freeShipping || '';
    }
    for (const el of [note, mirror]) {
      if (!el) continue;
      el.textContent = text;
      el.hidden = !text;
      el.classList.toggle('is-qualified', !!text && n >= 2);
    }
  }

  #renderReview() {
    const list = this.refs.reviewList;
    if (!list) return;
    list.innerHTML = '';

    let total = 0;
    const items = this.#collectLineItems();
    items.forEach((item) => {
      total += item.lineCents;
      const li = document.createElement('li');
      li.className = 'wh-bc__review-row';

      const imgSrc = item.thumbnail || item.image;
      if (imgSrc) {
        const img = document.createElement('img');
        img.src = imgSrc;
        img.alt = item.title;
        img.loading = 'lazy';
        li.appendChild(img);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'wh-bc__review-img-placeholder';
        li.appendChild(placeholder);
      }

      const title = document.createElement('p');
      title.className = 'wh-bc__review-title';
      title.textContent = item.title;
      li.appendChild(title);

      const price = document.createElement('span');
      price.className = 'wh-bc__review-price';
      price.textContent = this.#money(item.lineCents);
      li.appendChild(price);

      if (item.variantLabels) {
        const sub = document.createElement('span');
        sub.className = 'wh-bc__review-sub';
        sub.style.gridColumn = '2 / span 2';
        sub.style.gridRow = '2';
        sub.textContent = item.variantLabels;
        li.appendChild(sub);
      }

      // Every CART LINE gets its own stepper + remove (shop semantics: with
      // repeatable ADD, one category can hold several lines — each must be
      // adjustable here; the summary is the only place with a stepper).
      // Line-keyed ops; pre-hydration rows (no lineKey yet) render read-only
      // for the instant before /cart.js lands.
      if (item.lineKey) {
        const qty = document.createElement('div');
        qty.className = 'wh-bc__review-qty';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.textContent = '−';
        minus.disabled = item.qty <= 1;
        minus.addEventListener('click', () => {
          if (item.qty > 1) this.#queueLineQty(item.lineKey, item.qty - 1);
        });
        const qtyVal = document.createElement('span');
        qtyVal.textContent = String(item.qty);
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.textContent = '+';
        plus.addEventListener('click', () => {
          this.#queueLineQty(item.lineKey, item.qty + 1);
        });
        // Design order: + 1 −
        qty.appendChild(plus);
        qty.appendChild(qtyVal);
        qty.appendChild(minus);
        li.appendChild(qty);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'wh-bc__review-remove';
        remove.textContent = this.labels.remove;
        remove.addEventListener('click', () => this.#queueLineQty(item.lineKey, 0));
        li.appendChild(remove);
      }

      list.appendChild(li);
    });

    // Total from the REAL cart when hydrated (presentment-correct, covers any
    // line the payload can't price); summed payload prices before that.
    const cartTotal = this._cart ? this._cart.total_price : null;
    if (this.refs.reviewTotal) {
      this.refs.reviewTotal.textContent = this.#money(cartTotal != null ? cartTotal : total);
    }
    this.#renderCartButton(); // review qty/remove changes feed the cart count
    // The sheet's height just changed (content-driven on phones) — re-run
    // the summary camera fit AFTER layout settles so the free strip above
    // the sheet is measured true (user 2026-08-18: boxes hid behind a
    // 2-row sheet that grew after the fit had already run).
    if (this.scene && this.#isSummary(this.state.currentKey)) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.scene && this.#isSummary(this.state.currentKey) && this.scene.refitSummary) {
          this.scene.refitSummary();
        }
      }));
    }
  }

  #collectLineItems() {
    // The review list is the CART, verbatim (user shop semantics 2026-08-14:
    // repeatable ADD means one category can hold several lines — SP-005 and
    // SP-004A are both pads — and every line must be visible and adjustable;
    // the pick-per-category view hid the extras while the total counted them).
    // Oldest line first, matching the 3D's first-added-wins reading. Payload
    // products enrich rows (display title, thumbnail); lines the payload
    // can't resolve still render from the cart line's own fields.
    if (this._cart) {
      return (this._cart.items || []).slice().reverse().map((line) => {
        const found = this.#productForVariantId(line.variant_id);
        const subParts = [];
        if (found) {
          const variantLabels = this.#variantLabelsFor(found.product, found.variant);
          if (variantLabels) subParts.push(variantLabels);
          else if (found.product.colorName) subParts.push(found.product.colorName.toLowerCase());
        } else if (line.variant_title && line.variant_title.toLowerCase() !== 'default title') {
          subParts.push(line.variant_title.toLowerCase());
        }
        return {
          lineKey: line.key,
          // Same chain as the scenes (user 2026-08-21): per-product override →
          // spec display title → the cart line's real product title.
          title:
            (found && (found.product.titleOverride || found.product.bundleDisplayTitle)) ||
            line.product_title,
          lineCents: line.final_line_price,
          qty: line.quantity,
          thumbnail: found ? found.variant.thumbnail || found.product.thumbnail : null,
          image: line.image,
          variantLabels: subParts.join(' · '),
        };
      });
    }
    // Pre-hydration fallback (the instant before /cart.js lands): the picks,
    // priced from the payload, read-only (no lineKey).
    const items = [];
    this.realSteps.flatMap((s) => [s, ...(s.subSteps || [])]).forEach((step) => {
      const picked = this.#pickedFor(step.key);
      if (!picked) return;
      const product = this.#productById(step.key, picked.productId);
      if (!product) return;
      const variantId = picked.variantId || product.defaultVariantId;
      const variant = this.#variantById(product, variantId) || product.variants[0];
      const qty = picked.qty || 1;
      const subParts = [];
      const variantLabels = this.#variantLabelsFor(product, variant);
      if (variantLabels) subParts.push(variantLabels);
      else if (product.colorName) subParts.push(product.colorName.toLowerCase());
      items.push({
        lineKey: null,
        title: this.#displayTitleFor(product),
        lineCents: variant.priceCents * qty,
        qty,
        thumbnail: variant.thumbnail || product.thumbnail,
        image: variant.image || product.image,
        variantLabels: subParts.join(' · '),
      });
    });
    return items;
  }

  #variantLabelsFor(product, variant) {
    if (!variant) return '';
    const names = product.optionNames || [];
    return variant.options
      .map((v, i) => (v && names[i] ? `${v}` : null))
      .filter(Boolean)
      // Single-variant products carry Shopify's placeholder "Default Title"
      // — never something to show a customer.
      .filter((v) => v.toLowerCase() !== 'default title')
      .join(' · ')
      .toLowerCase();
  }

  // === Product views ("see more +") ===
  // One panel link opens a right drawer (same slot/styling as the campaign
  // switcher) listing this product's views. Only the campaign has a real URL;
  // specifications and images are payload-rendered canvas views with no page
  // of their own (user decision 2026-07-20).
  #openMoreNav(product, specs, { autoOpen = null } = {}) {
    const nav = this.refs.moreNav;
    const list = this.refs.moreList;
    if (!nav || !list) return;
    this.#closeMenu();
    // Callers without a resolved specs entry (campaign deep links) get the
    // product's default-variant specs.
    if (specs === undefined) {
      const variant = this.#variantById(product, this.state.variantByProduct[product.id] || product.defaultVariantId);
      specs = this.#specsFor(product, variant);
    }
    // Heading is per-product: the campaign entry's title, else the per-product
    // title override, else the fallback.
    if (this.refs.moreTitle) {
      this.refs.moreTitle.textContent =
        product.campaignTitle || product.titleOverride ||
        (product.useProductTitle && product.title) || this.labels.moreNav || '';
    }
    list.innerHTML = '';
    // Fixed item order (user spec 2026-07-31): Images & Specs always on top,
    // then About the product (campaign), FAQ, Articles, Reviews. Items
    // without content for this product don't render.
    // Each item may carry a `warm` — its fetch URL, prefetched on hover/focus
    // so the click is usually a cache hit (user item 7, 2026-08-07). Reviews
    // have none: their page-1 parse fetches uncached by design.
    const items = [];
    if ((product.images || []).length || specs || product.productDescription) {
      items.push({
        label: this.labels.moreImagesSpecs || 'images & specs',
        run: () => this.#openTemplateView(product, this.viewTemplates.specs || 'specs', this.labels.moreImagesSpecs || 'images & specs'),
        warm: () => this.#templateViewUrl(product, this.viewTemplates.specs || 'specs'),
      });
    }
    if (specs && specs.sizeGuide && specs.sizeGuide.html) {
      // Size guide (user 2026-08-27): the specifications entry's size_guide
      // reference (→ size_guides metaobject). Payload-rendered — no fetch.
      // Rides the resolved specs, so fetch_specs_from_variant products show
      // their variant's own guide.
      const sizeGuide = specs.sizeGuide;
      items.push({
        label: this.labels.moreSizeGuide || 'size guide',
        run: () => this.#openSizeGuideView(sizeGuide),
      });
    }
    if (product.campaignUrl) {
      items.push({
        label: this.labels.moreAbout || 'about the product',
        run: () => this.#openCampaign(product),
        warm: () => product.campaignUrl,
      });
    }
    if (product.faqUrl) {
      // Platform FAQ (product metafield custom.faq → metaobject web page,
      // merchant-designed nb26_faq template) — fills the slot reserved in
      // the 2026-07-31 order. Renders in the content canvas at its real URL.
      items.push({
        label: this.labels.moreFaq || 'faq',
        run: () => this.#openContent(product.faqUrl, product.faqTitle || this.labels.moreFaq || 'faq', { source: 'more' }),
        warm: () => product.faqUrl,
      });
    }
    if ((product.articles || []).length) {
      // Per-product featured articles (custom.featured_blog_posts metafield,
      // user spec 2026-08-03). Empty metafield = no item.
      items.push({
        label: this.labels.moreArticles || 'articles',
        run: () => this.#openTemplateView(product, this.viewTemplates.articles || 'articles', this.labels.moreArticles || 'articles', { articlesBack: true }),
        warm: () => this.#templateViewUrl(product, this.viewTemplates.articles || 'articles'),
      });
    }
    if (this._catalogIdx === 0 && product.reviewsCount > 0) {
      // Judge.me reviews — buyable (shop) products with at least one review
      // (user 2026-08-17: the keyboard showed an empty reviews tab). The
      // count is the standard reviews.rating_count metafield Judge.me keeps.
      items.push({ label: this.labels.moreReviews || 'reviews', run: () => this.#openReviewsView(product) });
    }
    if (!items.length) return; // panel link should already be hidden
    items.forEach(({ label, run, warm }) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wh-ex__pill wh-ex__campaign-item wh-ex-glass-hover';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        list.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
        btn.classList.add('is-active');
        run();
      });
      if (warm) {
        const kickoff = () => this.#prefetchContent(warm());
        btn.addEventListener('pointerenter', kickoff);
        btn.addEventListener('focus', kickoff);
      }
      li.appendChild(btn);
      list.appendChild(li);
    });
    clearTimeout(this._moreHideT);
    nav.hidden = false;
    void nav.offsetWidth;
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    this.#syncOverlayKeydown();
    // Default view: "see more +" lands on the specifications view beside the
    // menu (user spec 2026-07-21). Routed through the item's own button so
    // active-marking follows the normal path. No-op when the item is absent.
    if (autoOpen) {
      const target = [...list.querySelectorAll('.wh-ex__campaign-item')].find((b) => b.textContent === autoOpen);
      if (target) target.click();
    }
  }

  // Scene-driven "see more +" (user 2026-10-06): a click on the active product
  // or a downward scroll over the 3D view runs exactly what the panel's
  // "see more +" link runs (same per-product gating, same auto-open view,
  // same phone landing on the drawer). Inert while any overlay, dialog or
  // the summary is up, and when the product has no views (link hidden).
  #openMoreFromScene() {
    const link = this.refs.panelMore;
    if (!link || link.hidden || typeof link.onclick !== 'function') return false;
    if (this.#anyOverlayOpen() || document.querySelector('dialog[open]')) return false;
    if (this.#isSummary(this.state.currentKey)) return false;
    link.onclick();
    return true;
  }

  // Only the FIRST downward tick of a fresh wheel gesture opens the views:
  // trackpads keep emitting inertial ticks for a while, so a gesture that
  // is still coasting when the visitor closes the canvas must not reopen
  // it. A tick counts as fresh when the previous one is older than
  // WHEEL_GAP_MS; sideways-dominant ticks are ignored.
  #onSceneWheel = (e) => {
    const WHEEL_GAP_MS = 400;
    const now = performance.now();
    const fresh = now - (this._sceneWheelAt || 0) > WHEEL_GAP_MS;
    this._sceneWheelAt = now;
    if (!fresh || e.deltaY <= 0 || Math.abs(e.deltaX) > e.deltaY) return;
    this.#openMoreFromScene();
  };

  #closeMoreNav() {
    const nav = this.refs.moreNav;
    if (!nav) return;
    // Closing the views drawer closes its canvas view with it (user
    // 2026-08-18 — same rule as the menu). keepOverlay skips
    // #closeContent's own drawer/menu close, so no recursion; #closeContent
    // flips _contentOpen false before it ever calls back in here.
    if (this._contentOpen && this._contentSource === 'more') {
      this.#closeContent({ keepOverlay: true });
    }
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    clearTimeout(this._moreHideT);
    this._moreHideT = setTimeout(() => {
      nav.hidden = true;
    }, 320); // keep in sync with the drawer transition
    this.#syncOverlayKeydown();
  }

  // Canvas views rendered from the payload — no fetch, no URL, no history
  // writes (a hard refresh lands on the product URL, which is correct).
  // Parse a fetched theme page into its canvas-ready #MainContent element:
  // drop the explorer's own section, hoist same-origin MODULE scripts once
  // per URL and strip the rest, hoist stylesheet links our document lacks,
  // and promote blur-up placeholders (their swap script never runs in canvas
  // context). Shared by #openContent and the template views.
  #adoptFetchedMain(html, url) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pageTitle = doc.title || '';
    // `url` is often a RELATIVE path (product.url + query) — new URL() needs
    // an absolute base or it THROWS, silently dropping every module hoist
    // (bisected 2026-08-05: the gallery module never loaded in the canvas).
    const baseUrl = new URL(url, window.location.href);
    const main = doc.querySelector('#MainContent') || doc.body;
    Array.from(main.children).forEach((el) => {
      if (el.tagName === 'WH-EXPLORER' || el.querySelector('wh-explorer')) el.remove();
    });
    this._contentScripts = this._contentScripts || new Set();
    main.querySelectorAll('script').forEach((s) => {
      const scriptSrc = s.getAttribute('src');
      // APP BLOCK scripts (Hulk forms etc.) must run in the canvas — a
      // parser-inserted <script> is inert when adopted, so RECREATE it in
      // place (user 2026-08-18: forms only appeared after a hard refresh).
      // App-block external srcs dedupe per URL (app loaders are usually
      // idempotent singletons); inline init snippets re-run per open by
      // design. [data-wh-keep-scripts] is the theme-side opt-in for the same
      // treatment WITHOUT the dedupe — those scripts inject markup next to
      // their own tag (Cookiebot's declaration), and the canvas body is
      // rebuilt on every open, so they must re-run each time.
      const keeper = s.closest('.shopify-app-block, [data-wh-keep-scripts]');
      if (keeper) {
        const clone = document.createElement('script');
        for (const attr of s.attributes) clone.setAttribute(attr.name, attr.value);
        if (scriptSrc) {
          try {
            const u = new URL(scriptSrc, baseUrl);
            if (s.closest('.shopify-app-block')) {
              if (this._contentScripts.has(u.href)) {
                s.remove();
                return;
              }
              this._contentScripts.add(u.href);
            }
          } catch (_) {
            /* unparsable — recreate as-is */
          }
        } else {
          clone.textContent = s.textContent;
        }
        s.replaceWith(clone);
        return;
      }
      if (scriptSrc && (s.getAttribute('type') || '') === 'module') {
        try {
          const u = new URL(scriptSrc, baseUrl);
          if (u.origin === window.location.origin && !this._contentScripts.has(u.href)) {
            this._contentScripts.add(u.href);
            const tag = document.createElement('script');
            tag.type = 'module';
            tag.src = u.href;
            document.head.appendChild(tag);
          }
        } catch (_) {
          /* unparsable src — drop it */
        }
      }
      s.remove();
    });
    doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || document.querySelector(`link[rel="stylesheet"][href="${CSS.escape(href)}"]`)) return;
      const clone = document.createElement('link');
      clone.rel = 'stylesheet';
      clone.href = href;
      // Tracked: fetched-page CSS is GLOBAL while hoisted (it can restyle the
      // explorer chrome) — a full canvas close removes it again; a cache-hit
      // reopen re-hoists through this same path.
      this._contentStyles = this._contentStyles || new Set();
      this._contentStyles.add(clone);
      document.head.appendChild(clone);
    });
    // Hulk Form Builder (user 2026-08-19, second report): the form block is
    // an EMPTY .pxFormGenerator container that the app's LAYOUT-level embed
    // populates once at page load — adopted canvas DOM arrives after that
    // scan and stays empty until a hard refresh. Stash a hydration step the
    // opener runs AFTER the content is actually in the document: carry the
    // fetched page's form_data (the inline embed script lives OUTSIDE main)
    // and re-append the builder script — a fresh element re-executes even
    // from HTTP cache and fills empty containers.
    this._pendingFormHydration = null;
    if (main.querySelector('.pxFormGenerator')) {
      const dataScript = Array.from(doc.querySelectorAll('script')).find(
        (sc) => !sc.src && /hulkFormBuilder\s*=/.test(sc.textContent)
      );
      const builder =
        document.querySelector('script[src*="form-builder-script"]') ||
        doc.querySelector('script[src*="form-builder-script"]');
      this._pendingFormHydration = () => {
        if (dataScript) {
          const ds = document.createElement('script');
          ds.textContent = dataScript.textContent;
          document.head.appendChild(ds);
        }
        // The builder exposes window.GetCorePage(): rescans every
        // .pxFormGenerator and loads it (its own boot only runs on
        // DOMContentLoaded, which fired long before adopted content
        // existed — bisected from the CDN script 2026-08-19).
        if (typeof window.GetCorePage === 'function') {
          window.GetCorePage();
        } else if (builder && builder.src) {
          // Cold path: builder never loaded on this page — load it, then
          // rescan (its own DOMContentLoaded hook is dead by now).
          const bs = document.createElement('script');
          bs.src = builder.src;
          bs.onload = () => {
            if (typeof window.GetCorePage === 'function') window.GetCorePage();
          };
          document.head.appendChild(bs);
        }
      };
    }
    main.querySelectorAll('img[data-full]').forEach((img) => {
      img.removeAttribute('srcset');
      img.loading = 'lazy';
      img.src = img.dataset.full;
      img.classList.add('is-loaded');
      img.style.filter = 'none';
    });
    return { main, pageTitle };
  }

  // A See-more view rendered by a merchant-owned ALTERNATE PRODUCT TEMPLATE
  // (user architecture 2026-08-05): fetch <product.url>?view=<suffix>
  // (&variant=<id> so per-variant specs resolve) and lift the template's
  // sections into the canvas as a local view — no URL, same rune beat, same
  // per-URL cache as every content fetch. The suffix comes from the
  // section's "Canvas view templates" settings (this.viewTemplates), so the
  // merchant decides in the customizer which template renders each view.
  // articlesBack marks the view as the articles LIST: opening an article
  // from it stacks this view as the back target (the back-chevron hard rule).
  // The template view's fetch URL — ONE builder shared by the open AND the
  // hover prefetch, so the cache key always matches by construction.
  #templateViewUrl(product, suffix) {
    const base = product.url || (product.handle ? `/products/${product.handle}` : '');
    if (!base) return null;
    const variantId = this.state.variantByProduct[product.id] || product.defaultVariantId;
    let viewUrl = base + (base.includes('?') ? '&' : '?') + 'view=' + encodeURIComponent(suffix);
    if (variantId) viewUrl += '&variant=' + variantId;
    return viewUrl;
  }

  // ONE cached HTML fetch shared by every canvas path (#openContent,
  // #openTemplateView, #prefetchContent, reviews page 1). OK responses are
  // cached; the cache READ is skipped in the theme editor so opens stay
  // fresh there. Non-OK responses return their body + content type so the
  // caller decides (the themed 404 page is renderable HTML).
  async #fetchHtml(url) {
    const designMode = window.Shopify && window.Shopify.designMode;
    const cached = !designMode && this._contentCache.get(url);
    if (cached) {
      // Map-as-LRU: re-insert on hit so heavy sessions evict the stalest.
      this._contentCache.delete(url);
      this._contentCache.set(url, cached);
      return { ok: true, status: 200, html: cached, contentType: 'text/html' };
    }
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    const html = await res.text();
    if (res.ok) {
      this._contentCache.set(url, html);
      // Cap the cache — full page bodies accumulate fast on long sessions.
      while (this._contentCache.size > 20) {
        this._contentCache.delete(this._contentCache.keys().next().value);
      }
    }
    return { ok: res.ok, status: res.status, html, contentType: res.headers.get('content-type') || '' };
  }

  // Warm the content cache for a URL the visitor is ABOUT to open (hover /
  // focus on any canvas-opening control). Fills the SAME cache every open
  // path reads (#openContent, #openTemplateView), so whichever opener fires
  // gets an instant hit; already-cached and in-flight URLs are no-ops.
  // Best-effort by design: failures are dropped silently — the real open
  // refetches with its own error handling. Skipped in the theme editor
  // (opens bypass the cache there).
  async #prefetchContent(url) {
    if (!url || (window.Shopify && window.Shopify.designMode)) return;
    if (this._contentCache.has(url) || this._prefetching.has(url)) return;
    this._prefetching.add(url);
    try {
      await this.#fetchHtml(url);
    } catch (_) {
      /* best-effort */
    }
    this._prefetching.delete(url);
  }

  #openTemplateView(product, suffix, label, { articlesBack = false } = {}) {
    const wrap = document.createElement('div');
    wrap.className = 'wh-ex__view-template';
    let readyResolve;
    const ready = new Promise((r) => {
      readyResolve = r;
    });
    this.#openLocalView(label, wrap, { ready });
    const token = this._contentToken;
    if (articlesBack) this._localReopen = () => this.#openTemplateView(product, suffix, label, { articlesBack });
    (async () => {
      try {
        const viewUrl = this.#templateViewUrl(product, suffix);
        if (!viewUrl) return;
        const page = await this.#fetchHtml(viewUrl);
        if (!page.ok) throw new Error(`Failed to load (${page.status})`);
        const html = page.html;
        if (this._contentToken !== token) return; // superseded mid-fetch
        const { main } = this.#adoptFetchedMain(html, viewUrl);
        Array.from(main.children).forEach((child) => wrap.appendChild(document.adoptNode(child)));
        this.#appendCanvasFooter(wrap);
        if (this._pendingFormHydration) {
          this._pendingFormHydration();
          this._pendingFormHydration = null;
        }
      } catch (err) {
        console.warn('[wh-explorer] template view fetch failed', err);
        if (this._contentToken === token) wrap.textContent = (err && err.message) || 'Failed to load.';
      } finally {
        readyResolve();
      }
    })();
  }

  // Size guide view (user 2026-08-27): a payload-rendered LOCAL view — the
  // guide's content ships in the product payload (size_guides metaobject via
  // the specifications entry), so there is nothing to fetch. table_html is
  // merchant-authored HTML rendered verbatim inside the styled table wrap.
  #openSizeGuideView(sizeGuide) {
    const wrap = document.createElement('div');
    // NOT .wh-ex__view-template: local views keep the canvas's 24px side
    // inset (the template class opts out of it — merchant sections bring
    // their own).
    wrap.className = 'wh-ex__sizeguide';
    if (sizeGuide.heading) {
      const h = document.createElement('h2');
      h.className = 'wh-ex__sizeguide-heading';
      h.textContent = sizeGuide.heading;
      wrap.appendChild(h);
    }
    const table = document.createElement('div');
    table.className = 'wh-ex__sizeguide-table';
    table.innerHTML = sizeGuide.html;
    wrap.appendChild(table);
    if (sizeGuide.text) {
      const p = document.createElement('p');
      p.className = 'wh-ex__sizeguide-text';
      p.textContent = sizeGuide.text;
      wrap.appendChild(p);
    }
    if (sizeGuide.image) {
      const img = document.createElement('img');
      img.className = 'wh-ex__sizeguide-image';
      img.src = sizeGuide.image;
      img.alt = sizeGuide.heading || '';
      img.loading = 'lazy';
      wrap.appendChild(img);
    }
    this.#openLocalView(this.labels.moreSizeGuide || 'size guide', wrap);
  }

  // Append a fresh clone of the canvas footer (the [WH] Canvas footer
  // section in the explorer group renders a hidden SOURCE on every page) to
  // the end of a canvas view. One implementation covers pages, blog posts,
  // product templates and policies alike — no per-template placement.
  #appendCanvasFooter(container) {
    const source = document.getElementById('wh-canvas-footer-source');
    if (!source || !container) return;
    // [WH] Canvas overrides can opt a template out of the shared footer.
    const marker = container.querySelector('[data-wh-canvas-overrides]');
    if (marker && marker.dataset.hideFooter === 'true') return;
    const footer = source.firstElementChild;
    if (footer) container.appendChild(footer.cloneNode(true));
  }

  // [WH] Canvas overrides — a hidden marker section inside a template flips
  // the canvas from glass to a real page: section backgrounds render
  // (the strip rules are gated on :not(.wh-ex--tpl-bg)) and the sheet goes
  // opaque in the marker's color (campaign pages, user spec 2026-08-13). The
  // topbar and the canvas frame stay. Re-read on EVERY injection, so views
  // without a marker always reset to glass; pass null to reset explicitly.
  #applyCanvasOverrides(container) {
    const content = this.refs.content;
    if (!content) return;
    const marker = container && container.querySelector('[data-wh-canvas-overrides]');
    const on = !!marker && marker.dataset.showBackgrounds === 'true';
    content.classList.toggle('wh-ex--tpl-bg', on);
    if (on && marker.dataset.background) {
      content.style.setProperty('--wh-ex-canvas-bg', marker.dataset.background);
    } else {
      content.style.removeProperty('--wh-ex-canvas-bg');
    }
  }

  // Canvas loader beat: clear the body + show the rune. Paired with
  // #canvasLoaderFinish, which resolves after AT LEAST CANVAS_LOADER_MIN_MS
  // (extended by however long the caller's real work takes) and hides the
  // rune — unless another view superseded this one in the meantime.
  #canvasLoaderStart() {
    if (this.refs.contentBody) this.refs.contentBody.replaceChildren();
    // The overrides marker left with the old content — back to glass until
    // the next injection re-applies its own marker.
    this.#applyCanvasOverrides(null);
    if (this.refs.contentLoader) this.refs.contentLoader.hidden = false;
    this._canvasLoaderAt = performance.now();
  }

  async #canvasLoaderFinish(token) {
    const waited = performance.now() - (this._canvasLoaderAt || 0);
    const remaining = Math.max(0, CANVAS_LOADER_MIN_MS - waited);
    if (remaining) await new Promise((r) => setTimeout(r, remaining));
    if (token !== this._contentToken || !this._contentOpen) return false; // superseded
    if (this.refs.contentLoader) this.refs.contentLoader.hidden = true;
    return true;
  }

  #openLocalView(title, node, { ready = null } = {}) {
    const content = this.refs.content;
    const body = this.refs.contentBody;
    if (!content || !body) return;
    // A pending close transition would re-hide the canvas mid-open.
    clearTimeout(this._contentHideT);
    this._contentOpen = true;
    this._contentSource = 'more';
    this._contentToken = Symbol('content');
    // Local views are canvas ROOTS (opened from the views drawer): no URL,
    // nothing to go back to. A view that leads deeper (the articles list)
    // sets _localReopen after this call so #openContent can stack it.
    this._canvasStack = [];
    this._contentUrl = null;
    this._localReopen = null;
    this.#renderCanvasBack();
    if (this.refs.contentTitle) this.refs.contentTitle.textContent = title || '';
    content.hidden = false;
    void content.offsetWidth;
    content.classList.add('is-open');
    content.setAttribute('aria-hidden', 'false');
    this.#syncOverlayKeydown();
    // The universal rune beat (CANVAS_LOADER_MIN_MS): local views build
    // synchronously, but they get the same transition as fetched pages —
    // `ready` (optional promise, e.g. the reviews fetch) extends it.
    this.#canvasLoaderStart();
    const token = this._contentToken;
    Promise.resolve(ready)
      .catch(() => {})
      .then(async () => {
        const ok = await this.#canvasLoaderFinish(token);
        if (!ok) return;
        body.replaceChildren(node);
        this.#applyCanvasOverrides(body);
        body.scrollTop = 0;
      });
  }

  // Reviews view — PLAIN unbranded list (user directive 2026-08-04): no
  // Judge.me widget markup, CSS, or engine in the canvas. We fetch the data
  // and render it ourselves as stacked black text. Judge.me v3 has no public
  // JSON API for review content, so both reads piggyback on what the app
  // already serves:
  //   page 1 — templates/product.reviews.json ("?view=reviews", layout:
  //     false) renders the review_widget app block server-side; we parse the
  //     .jdgm-rev nodes + average/count + the paginate element's
  //     data-url/per-page, then throw the markup away.
  //   pages 2+ — that data-url (api.judge.me/reviews/reviews_for_widget),
  //     the same public endpoint the widget's own pagination calls; returns
  //     { html, total_count } parsed identically.
  #parseReviewNodes(root) {
    return [...root.querySelectorAll('.jdgm-rev')].map((el) => {
      const pick = (sel) => {
        const n = el.querySelector(sel);
        return n ? n.textContent.trim() : '';
      };
      const rating = el.querySelector('.jdgm-rev__rating');
      const time = el.querySelector('time');
      let paras = [...el.querySelectorAll('.jdgm-rev__body p')].map((p) => p.textContent.trim());
      if (!paras.length) paras = [pick('.jdgm-rev__body')];
      return {
        score: rating ? parseInt(rating.getAttribute('data-score') || '0', 10) : 0,
        time: time ? time.getAttribute('datetime') || '' : '',
        author: pick('.jdgm-rev__author'),
        location: pick('.jdgm-rev__location').replace(/^\(|\)$/g, ''),
        title: pick('.jdgm-rev__title'),
        body: paras.filter(Boolean),
      };
    });
  }

  async #fetchReviewsPage(product, page, meta) {
    try {
      if (page === 1) {
        // ONE URL builder with the open/prefetch paths (cache keys match by
        // construction, and the &variant= the inline rebuild dropped is back).
        const viewUrl = this.#templateViewUrl(product, this.viewTemplates.reviews || 'reviews');
        if (!viewUrl) return null;
        const page1 = await this.#fetchHtml(viewUrl);
        if (!page1.ok) return null;
        const doc = new DOMParser().parseFromString(page1.html, 'text/html');
        const widg = doc.querySelector('.jdgm-rev-widg');
        if (!widg) return null;
        const pag = doc.querySelector('.jdgm-paginate');
        return {
          average: widg.getAttribute('data-average-rating') || '',
          total: parseInt(widg.getAttribute('data-number-of-reviews') || '0', 10),
          perPage: parseInt((pag && pag.getAttribute('data-per-page')) || '5', 10),
          apiUrl: (pag && pag.getAttribute('data-url')) || '',
          reviews: this.#parseReviewNodes(doc),
        };
      }
      if (!meta || !meta.apiUrl) return null;
      const shop = (window.Shopify && window.Shopify.shop) || document.domain;
      const params = new URLSearchParams({
        url: shop,
        shop_domain: shop,
        platform: 'shopify',
        page: String(page),
        per_page: String(meta.perPage),
        product_id: String(product.id),
      });
      const lang = document.documentElement.lang;
      if (lang && window.jdgmSettings && window.jdgmSettings.enable_multi_locales_translations) {
        params.set('translation_locale', lang);
      }
      const res = await fetch(meta.apiUrl + '?' + params.toString(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      const tpl = document.createElement('template');
      tpl.innerHTML = data.html || '';
      return { reviews: this.#parseReviewNodes(tpl.content) };
    } catch (_) {
      return null;
    }
  }

  #renderReviews(listEl, reviews) {
    for (const r of reviews) {
      const item = document.createElement('div');
      item.className = 'wh-ex__rev';
      if (r.score > 0) {
        const stars = document.createElement('p');
        stars.className = 'wh-ex__rev-stars';
        stars.textContent = '★★★★★'.slice(0, r.score) + '☆☆☆☆☆'.slice(0, 5 - r.score);
        item.appendChild(stars);
      }
      if (r.title) {
        const t = document.createElement('p');
        t.className = 'wh-ex__rev-title';
        t.textContent = r.title;
        item.appendChild(t);
      }
      for (const para of r.body) {
        const p = document.createElement('p');
        p.className = 'wh-ex__rev-body';
        p.textContent = para;
        item.appendChild(p);
      }
      const metaLine = [r.author, r.location, r.time ? new Date(r.time).toLocaleDateString() : '']
        .filter(Boolean)
        .join(' — ');
      if (metaLine) {
        const m = document.createElement('p');
        m.className = 'wh-ex__rev-meta';
        m.textContent = metaLine;
        item.appendChild(m);
      }
      listEl.appendChild(item);
    }
  }

  #openReviewsView(product) {
    const wrap = document.createElement('div');
    wrap.className = 'wh-ex__view-reviews';
    const head = document.createElement('p');
    head.className = 'wh-ex__rev-summary';
    const list = document.createElement('div');
    list.className = 'wh-ex__revs';
    wrap.append(head, list);
    // The reviews fetch is real network work: it extends the canvas rune
    // beat via `ready` (the wrap stays disconnected until the beat ends, so
    // supersession is tracked by token, not connection).
    let readyResolve;
    const ready = new Promise((r) => {
      readyResolve = r;
    });
    this.#openLocalView(this.labels.moreReviews || 'reviews', wrap, { ready });
    const viewToken = this._contentToken;
    let meta = null;
    let shown = 0;
    let page = 0;
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'wh-ex__rev-more';
    more.textContent = 'show more +';
    more.onclick = () => load(page + 1);
    const load = async (n) => {
      more.remove();
      try {
        const res = await this.#fetchReviewsPage(product, n, meta);
        if (this._contentToken !== viewToken) return; // view superseded mid-fetch
        if (n === 1) {
          if (!res || !res.total) {
            head.textContent = 'No reviews yet.';
            return;
          }
          meta = res;
          head.textContent = `${res.average} / 5 — ${res.total} reviews`;
        }
        if (res && res.reviews.length) {
          this.#renderReviews(list, res.reviews);
          shown += res.reviews.length;
          page = n;
          if (shown < meta.total) wrap.appendChild(more);
        }
      } finally {
        if (n === 1) readyResolve();
      }
    };
    load(1);
  }

  // === Cart engine ===
  // The real Shopify cart backs the picks. Every write is optimistic in the
  // UI (picks/chips update instantly), goes through ONE serialized queue
  // (concurrent clicks can't interleave add/change requests), and ends by
  // re-fetching /cart.js and reconciling state from it (#applyCart). Line
  // items are tagged with hidden properties: _wh_category (the category key —
  // how a line maps back to a pick) and _wh_loadout (the catalog handle).

  #cartRoutes() {
    const r = (typeof Theme !== 'undefined' && Theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: (r.cart_change_url || '/cart/change') + '.js',
      cart: (r.cart_url || '/cart') + '.js',
      checkout: '/checkout',
    };
  }

  // `revert` is a key-scoped rollback of the optimistic state the caller
  // wrote at click time. It runs FIRST on failure — locally, so the UI
  // recovers even when the follow-up server re-sync is itself unreachable —
  // and the /cart.js re-fetch then reconciles when the network allows (late
  // sell-outs surface here as a failed add + banner).
  #enqueueCart(op, revert) {
    this._cartOpsInFlight = (this._cartOpsInFlight || 0) + 1;
    const done = () => { this._cartOpsInFlight -= 1; };
    this._cartQueue = this._cartQueue.then(op).finally(done).catch((e) => {
      console.error('[wh-explorer] cart operation failed', e);
      this.#flashError((e && e.message) || 'Cart update failed');
      if (revert) {
        try {
          revert();
        } catch (_) {
          /* rollback must never throw */
        }
        this.#renderStepsNav();
        this.#renderPanel();
        if (this.#isSummary(this.state.currentKey)) {
          this.#renderReview();
          this.#renderScene();
        }
        this.#updateUrl();
      }
      return this.#fetchCart()
        .then((cart) => this.#applyCart(cart))
        .catch(() => {});
    });
    return this._cartQueue;
  }

  async #fetchCart() {
    const res = await fetch(this.#cartRoutes().cart, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async #cartAdd(variantId, quantity, stepKey) {
    const properties = { _wh_category: stepKey, _wh_loadout: this.data.loadout.handle };
    // Pre-order note rides the line as a VISIBLE property (no underscore) so
    // the customer sees it in cart/checkout/order — e.g. the delayed JP
    // keyboard layout carries its notice onto the order itself.
    const found = this.#productForVariantId(variantId);
    const preOrder = found ? this.#preOrderTextFor(found.product, found.variant) : '';
    if (preOrder) properties['Pre-order'] = preOrder;
    const res = await fetch(this.#cartRoutes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties }],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Cart add failed');
    }
  }

  async #cartChange(lineKey, quantity) {
    const res = await fetch(this.#cartRoutes().change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Cart change failed');
    }
  }

  // Rebuild picks from the live cart — the single reconcile point. The
  // FIRST-ADDED line per category wins (user rule 2026-08-14: the first item
  // added in a category is the one whose packaging shows on the summary).
  // Shopify PREPENDS new lines to cart.items, so oldest-first means
  // iterating the array REVERSED; with multiple lines per category the
  // extras are expected now (multi-add) — no warn. seedBrowse mirrors the
  // picked config into the browse state — ONLY on the initial hydrate /
  // external re-syncs; own-write reconciles must never clobber what the
  // visitor is currently browsing.
  #applyCart(cart, { seedBrowse = false } = {}) {
    if (!cart) return;
    this._cart = cart;
    this._pendingAdds = 0; // server truth arrived — the badge reads the cart
    const picks = {};
    (cart.items || []).slice().reverse().forEach((item) => {
      const key = item.properties && item.properties._wh_category;
      if (!key || !this.categories.has(key) || this.#isSummary(key)) return;
      if (picks[key]) return; // later lines in the same category: expected (multi-add)
      const product = this.#productById(key, item.product_id);
      if (!product) return;
      picks[key] = {
        productId: item.product_id,
        variantId: item.variant_id,
        qty: item.quantity,
        lineKey: item.key,
      };
      if (seedBrowse) {
        const browse = this.state.browse[key];
        if (browse) {
          browse.productId = item.product_id;
          browse.variantId = item.variant_id;
          browse.qty = item.quantity;
          this.state.variantByProduct[item.product_id] = item.variant_id;
        }
      }
    });
    this.state.picks = picks;
    this.#renderStepsNav();
    this.#renderPanel();
    if (this.#isSummary(this.state.currentKey)) {
      this.#renderReview();
      this.#renderScene();
    } else if (seedBrowse) {
      // The seeded browse may change which variant models show on the rail.
      this.#renderScene();
    }
    this.#updateUrl();
  }

  // Boot/pageshow hydrate: picks (and, on boot, browse) restored from the
  // live cart — the explorer survives reloads and round-trips to checkout.
  async #hydrateCart() {
    try {
      const cart = await this.#fetchCart();
      this.#applyCart(cart, { seedBrowse: true });
    } catch (e) {
      console.warn('[wh-explorer] cart hydrate failed', e);
    }
  }

  // Announce a completed write through Horizon's standard cart event so every
  // other cart consumer (header bubbles on peripheral pages via bfcache,
  // future drawers) stays coherent. Resolves with the fresh cart we already
  // hold — announce only, no second mutation.
  #announceCart(cart, action, lines = []) {
    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({ action, context: 'explorer', lines, promise: deferred.promise })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'wh-explorer' },
      });
    } catch (e) {
      console.warn('[wh-explorer] cart announce failed', e);
    }
  }

  // External cart changes (anything not announced by us) re-sync the picks.
  #onCartLinesUpdate = (event) => {
    const p = event && event.promise;
    if (!p || typeof p.then !== 'function') return;
    p.then((resolved) => {
      const source = resolved && resolved.detail && resolved.detail.source;
      if (source === 'wh-explorer') return;
      // While OUR writes are in flight, a foreign re-sync must not seed the
      // browse state — it would fetch a pre-add cart and yank the product
      // the visitor is looking at (race found in the launch QA). Picks still
      // reconcile; the queued op's own refetch lands the final truth.
      const seedBrowse = !(this._cartOpsInFlight > 0);
      return this.#fetchCart().then((cart) => this.#applyCart(cart, { seedBrowse }));
    }).catch(() => {});
  };

  // Category-scoped write ops. Each reads the CURRENT cart line for its
  // category INSIDE the queued op (never at click time — a queued earlier op
  // may change it), mutates, re-fetches, reconciles, announces.

  #queueCommit(stepKey, variantId, qty, prevPick) {
    return this.#enqueueCart(
      async () => {
        // PURE add (user 2026-08-14): no zero-then-add — that was bundle
        // REPLACE semantics and silently removed the category's previous
        // product when a second one was added. Identical variant+properties
        // merge into one line (qty +1); different products coexist.
        await this.#cartAdd(variantId, qty, stepKey);
        const cart = await this.#fetchCart();
        this.#applyCart(cart);
        this.#announceCart(cart, 'add', [{ merchandiseId: String(variantId), quantity: qty }]);
      },
      () => {
        if (prevPick) this.state.picks[stepKey] = prevPick;
        else delete this.state.picks[stepKey];
      }
    );
  }

  // Line-keyed write for the review list (shop semantics): optimistically
  // mutate the local cart copy and reconcile through #applyCart (which also
  // rebuilds picks — removing a category's first-added line promotes the
  // next line to the pick, and the summary scene re-lays accordingly). On
  // failure #enqueueCart's catch re-fetches the real cart, so no manual
  // revert is needed.
  #queueLineQty(lineKey, qty) {
    if (this._cart) {
      const local = { ...this._cart };
      local.items = (this._cart.items || [])
        .map((l) => (l.key === lineKey ? { ...l, quantity: qty, final_line_price: l.final_price * qty } : l))
        .filter((l) => l.quantity > 0);
      local.item_count = local.items.reduce((n, l) => n + l.quantity, 0);
      local.total_price = local.items.reduce((n, l) => n + l.final_line_price, 0);
      this.#applyCart(local);
    }
    return this.#enqueueCart(async () => {
      await this.#cartChange(lineKey, qty);
      const cart = await this.#fetchCart();
      this.#applyCart(cart);
      this.#announceCart(cart, 'update');
    });
  }

  #flashError(message) {
    const banner = this.refs.banner;
    if (!banner) return;
    banner.textContent = message;
    banner.hidden = false;
    clearTimeout(this._bannerT);
    this._bannerT = setTimeout(() => {
      banner.hidden = true;
    }, 4000);
  }

  // The summary's CTA: items are already in the real cart — just go pay.
  #onCheckout = () => {
    window.location.href = this.#cartRoutes().checkout;
  };

  // === Share / permalink ===

  // Share = current URL + the browse state encoded ON DEMAND (the address
  // bar itself stays clean — see #updateUrl). Keyed shape: {l, c, s:[{k,p,
  // v,q}]}; added categories encode their PICK, un-added ones the browsed
  // state. Picks are never hydrated from a link (receiver's cart is truth).
  #onShare = async () => {
    this.#updateUrl();
    const stateObj = {
      l: this.data.loadout.handle,
      c: this.state.currentKey,
      s: this.realSteps.flatMap((s) => [s, ...(s.subSteps || [])]).map((step) => {
        const picked = this.#pickedFor(step.key);
        const browse = this.state.browse[step.key] || {};
        return {
          k: step.key,
          p: picked ? picked.productId : browse.productId,
          v: picked ? picked.variantId : browse.variantId,
          q: browse.qty || 1,
        };
      }),
    };
    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('wh-bundle', this.#base64UrlEncode(JSON.stringify(stateObj)));
    try {
      await navigator.clipboard.writeText(shareUrl.toString());
      if (this.refs.shareLabel) {
        const orig = this.refs.shareLabel.textContent;
        this.refs.shareLabel.textContent = this.labels.shareCopied || 'Copied';
        clearTimeout(this._shareT); // a second fast share must not restore mid-flash
        this._shareT = setTimeout(() => {
          this.refs.shareLabel.textContent = orig;
        }, 1500);
      }
    } catch (e) {
      console.warn('[wh-explorer] Clipboard write failed', e);
    }
  };

  #updateUrl() {
    const url = new URL(window.location.href);
    // The browse-state param is SHARE-ONLY (user 2026-08-18: plain visits
    // to / were getting the giant mirror written into the address bar).
    // #onShare builds it on demand; hydration consumed any incoming one, so
    // the address bar stays clean while browsing.
    url.searchParams.delete('wh-bundle');

    // While the content canvas is open the address bar belongs to the content
    // URL (#openContent pushed it) — scene-state writes must not stomp it.
    // #closeContent clears the flag before calling back in here.
    if (this._contentOpen) return;
    // Router write-side. Path changes only in full route mode, never in the
    // theme editor, and only once armed (the cold load keeps the server path —
    // '/' stays '/', a permalink-hydrated view doesn't yank the URL). All
    // other query params (?view=, ?preview_theme_id=, utm) are preserved by
    // construction — we only ever touch our own params and the pathname.
    const designMode = window.Shopify && window.Shopify.designMode;
    let push = false;
    if (this.routeMode === 'full' && this._routeArmed && !designMode) {
      const target = this.#routePathFor(this.state.currentKey);
      if (target && target !== url.pathname) {
        url.pathname = target;
        push = true; // a real view change — Back should return to the previous one
      }
      const browse = this.state.browse[this.state.currentKey];
      const product =
        browse && !this.#isSummary(this.state.currentKey)
          ? this.#productById(this.state.currentKey, browse.productId)
          : null;
      // ?variant= mirrors a non-default browsed variant (replaceState — variant
      // hops shouldn't pollute Back).
      if (product && browse.variantId && browse.variantId !== product.defaultVariantId) {
        url.searchParams.set('variant', String(browse.variantId));
      } else {
        url.searchParams.delete('variant');
      }
      // A product can live in several categories (keyboard accessories is a
      // curated subset of accessories), and on reload the path alone always
      // re-resolves to the FIRST step containing the handle. Pin the browsed
      // category with ?wh-cat only when it differs from that default
      // resolution; unambiguous paths stay clean.
      const def = product ? this.#findByHandle(product.handle) : null;
      if (def && def.stepKey !== this.state.currentKey) {
        url.searchParams.set('wh-cat', this.state.currentKey);
      } else {
        url.searchParams.delete('wh-cat');
      }
      this.#syncTitle(product);
    }
    if (push) {
      window.history.pushState({ whEx: true }, '', url.toString());
    } else {
      // Preserve foreign history.state (scroll-container stamps scroll
      // positions into it) instead of clobbering with a fresh object.
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  #hydrateFromUrl() {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('wh-bundle');
    if (!raw) return;
    let decoded;
    try {
      decoded = JSON.parse(this.#base64UrlDecode(raw));
    } catch (e) {
      console.warn('[wh-explorer] invalid wh-bundle param', e);
      return;
    }
    if (!decoded || decoded.l !== this.data.loadout.handle) return;

    const entries = (Array.isArray(decoded.s) ? decoded.s : [])
      .map((entry) => ({ key: entry.k, p: entry.p, v: entry.v, q: entry.q }))
      .filter((e) => e.key && this.categories.has(e.key) && !this.#isSummary(e.key));
    let totalRequested = 0;
    let totalLoaded = 0;
    entries.forEach((entry) => {
      const step = this.categories.get(entry.key);
      const browse = this.state.browse[entry.key];
      if (!browse) return;
      totalRequested += 1;
      const product = step.products.find((p) =>
        p.variants.some((v) => v.id === entry.v) || p.id === entry.p
      );
      if (product) {
        browse.productId = product.id;
        // A stale link's variant may have sold out since sharing — fall back
        // to the default (server-resolved first AVAILABLE variant).
        const variantMatch = product.variants.find((v) => v.id === entry.v && v.available !== false);
        browse.variantId = variantMatch ? variantMatch.id : product.defaultVariantId;
        this.state.variantByProduct[product.id] = browse.variantId;
        // qty is pre-seeded to 1; only an explicit q > 1 overrides it.
        if (typeof entry.q === 'number' && entry.q > 1) {
          browse.qty = entry.q;
        }
        totalLoaded += 1;
      }
    });
    // Picks are NOT hydrated from links — they mirror the receiver's REAL
    // cart (#hydrateCart); opening a link must never mutate someone's cart.
    // Clamp the active step to a navigable key (same gate as #goToStep) so a
    // stale URL can't land on a blank/nonexistent step.
    const requestedKey = decoded.c;
    if (requestedKey && this.categories.has(requestedKey)) {
      this.state.currentKey = requestedKey;
    }

    if (totalRequested > 0 && totalLoaded < totalRequested && this.refs.banner) {
      // Transient (user 2026-08-12): the URL mirror means most "shared
      // links" are just the visitor's own address bar — a permanent yellow
      // banner over stale test picks was pure noise. Flash + auto-hide.
      this.#flashError(this.labels.linkLoadedPartial);
    }
    // No auto-open — the explorer is always on; #start (called right after
    // hydration in connectedCallback) renders the hydrated state.
  }

  #disposeScene() {
    if (this.scene) {
      this.scene.dispose();
      this.scene = null;
    }
    // Reset the boot guard (the module promise stays cached — a re-open reuses
    // the already-downloaded module and boots instantly).
    this._sceneBooting = false;
    this._pendingSceneOpts = null;
    if (this.loaderTimeout) { clearTimeout(this.loaderTimeout); this.loaderTimeout = null; }
    clearTimeout(this._preloaderT);
    clearTimeout(this._veilHideT);
    this._veilBusy = false;
    // Reset the veil so a reconnect (editor section reload) starts clean.
    if (this.refs.veil) { this.refs.veil.classList.remove('is-on'); this.refs.veil.hidden = true; }
    if (this.refs.stage) this.refs.stage.innerHTML = '';
  }

  // === Transition veil (pre-loader / scene crossfades / summary load screen) ===

  #veilShow(rune) {
    const v = this.refs.veil;
    if (!v) return;
    clearTimeout(this._veilHideT);
    v.classList.toggle('wh-bc__veil--rune', !!rune);
    v.hidden = false;
    void v.offsetWidth; // reflow so the opacity transition runs same-frame
    v.classList.add('is-on');
  }

  #veilHide() {
    const v = this.refs.veil;
    if (!v) return;
    v.classList.remove('is-on');
    clearTimeout(this._veilHideT);
    this._veilHideT = setTimeout(() => { v.hidden = true; }, VEIL_FADE_MS + 50);
  }

  // Fade the veil in, run the action UNDER it, fade out. `hold` extends the
  // covered time (the summary's rune load screen). Overlapping requests run
  // the action bare rather than stacking veils — rapid nav stays responsive.
  async #transition({ rune = false, hold = 0 } = {}, action) {
    if (!this.refs.veil || this._veilBusy) { action(); return; }
    this._veilBusy = true;
    this.#veilShow(rune);
    await new Promise((r) => setTimeout(r, VEIL_FADE_MS + hold));
    action();
    await new Promise((r) => setTimeout(r, 60)); // let the new scene paint under the veil
    this.#veilHide();
    this._veilBusy = false;
  }

  // Drop the open pre-loader (scene reported ready, or the safety timeout
  // fired) — but never before PRELOADER_MIN_MS: loads are cache-fast, the rune
  // beat is deliberate.
  #hideLoader() {
    if (this.loaderTimeout) { clearTimeout(this.loaderTimeout); this.loaderTimeout = null; }
    const elapsed = performance.now() - (this._openedAt || 0);
    const min = this._preloaderMinMs ?? PRELOADER_MIN_MS;
    const remaining = Math.max(0, min - elapsed);
    clearTimeout(this._preloaderT);
    this._preloaderT = setTimeout(() => this.#veilHide(), remaining);
  }

  #money(cents) {
    // Shopify Liquid money AND the AJAX cart deliver every currency as
    // x100 "cents" — including zero-decimal ones (KRW, JPY). Core
    // formatMoney divides by the currency's NATIVE precision (10^0 = 1 for
    // KRW), which displayed Korean prices x100 (user 2026-08-19). Convert
    // to true minor units first; 2-decimal currencies pass through as-is.
    const currency = this.labels.currency || '';
    let precision = 2;
    try {
      precision = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits;
    } catch (_) {
      /* unknown code — keep 2 (no-op conversion) */
    }
    const minor = precision === 2 ? cents : Math.round((cents / 100) * Math.pow(10, precision));
    return formatMoney(minor, this.labels.currencyFormat, this.labels.currency);
  }

  #base64UrlEncode(str) {
    return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  #base64UrlDecode(str) {
    const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
    return decodeURIComponent(escape(atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad)));
  }
}

if (!customElements.get('wh-explorer')) {
  customElements.define('wh-explorer', WhExplorer);
}
